<template>
  <div class="flex h-screen bg-[#F8FAFC] font-sans text-slate-900 overflow-hidden">
    <!-- Import de 20 polices Google Fonts -->
    <link href="https://fonts.googleapis.com/css2?family=Roboto:wght@300;400;500;700;900&family=Open+Sans:wght@300;400;600;700;800&family=Montserrat:wght@300;400;500;600;700;800;900&family=Lato:wght@300;400;700;900&family=Poppins:wght@300;400;500;600;700;800;900&family=Raleway:wght@300;400;500;600;700;800;900&family=Ubuntu:wght@300;400;500;700&family=Playfair+Display:wght@400;500;600;700;800;900&family=Merriweather:wght@300;400;700;900&family=Oswald:wght@300;400;500;600;700&family=Source+Sans+Pro:wght@300;400;600;700;900&family=Nunito:wght@300;400;600;700;800;900&family=PT+Sans:wght@400;700&family=Quicksand:wght@300;400;500;600;700&family=Bebas+Neue&family=Inter:wght@300;400;500;600;700;800;900&family=Plus+Jakarta+Sans:wght@300;400;500;600;700;800&family=Space+Grotesk:wght@300;400;500;600;700&family=DM+Sans:wght@400;500;700&family=Work+Sans:wght@300;400;500;600;700;800;900&display=swap" rel="stylesheet">

<aside class="w-80 bg-[#F8FAFC] border-r border-slate-200/60 flex flex-col z-20 h-screen select-none">
  
  <div class="p-6 pb-8">
    <div class="flex items-center gap-3 mb-1">
      <div class="w-8 h-8 bg-indigo-600 rounded-lg shadow-lg shadow-indigo-200 flex items-center justify-center">
        <span class="text-white text-xs font-black italic">DP</span>
      </div>
      <h1 class="text-lg font-bold tracking-tight text-slate-900">Écran Perso</h1>
    </div>
    <div class="inline-flex items-center px-2 py-0.5 rounded-full bg-slate-200/50 text-[10px] font-bold text-slate-500 uppercase tracking-tighter">
      Builder CMS v2.0
    </div>

    <!-- ✅ NOUVEAU : Sélecteur d'écran -->
    <div class="mt-4 space-y-2">
      <label class="text-[10px] font-bold text-slate-500 ml-1 uppercase flex items-center gap-2">
        <svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-width="2" d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"/>
        </svg>
        Écran à configurer
      </label>
      <select v-model="selectedDeviceId" @change="loadScreenConfig"
              class="w-full p-2.5 bg-white border-2 border-slate-200 rounded-xl text-xs font-bold text-slate-700 focus:border-indigo-500 transition-all">
        <option :value="null">📱 Configuration par défaut (tous les écrans)</option>
        <option v-for="screen in availableScreens" :key="screen._id" :value="screen._id">
          <span v-if="screen.online">🟢</span>
          <span v-else>⚫</span>
          {{ screen.name || screen.identifier }}
          <span v-if="screen.hasCustomConfig"> ⭐</span>
        </option>
      </select>
      <p class="text-[9px] text-slate-400 italic px-1">
        <span v-if="selectedDeviceId">Configuration spécifique pour cet écran</span>
        <span v-else>Configuration partagée par tous les écrans sans config spécifique</span>
      </p>
    </div>
  </div>

  <div class="flex-1 overflow-y-auto px-4 space-y-8 custom-scrollbar">

    <!-- Templates -->
    <div class="space-y-4">
      <div class="flex items-center justify-between px-2">
        <h3 class="text-[11px] font-bold text-slate-400 uppercase tracking-widest">📋 Templates</h3>
        <span class="h-px flex-1 bg-slate-200 ml-4"></span>
      </div>

      <div class="grid grid-cols-2 gap-2">
        <button v-for="template in templates" :key="template.name"
                @click="applyTemplate(template)"
                class="p-3 bg-white border border-slate-200 rounded-xl hover:border-indigo-400 hover:shadow-md transition-all text-center group">
          <div class="text-2xl mb-1">{{ template.icon }}</div>
          <div class="text-[10px] font-bold text-slate-700 group-hover:text-indigo-600">{{ template.name }}</div>
        </button>
      </div>
    </div>

    <!-- Contrôles rapides -->
    <div class="space-y-4">
      <div class="flex items-center justify-between px-2">
        <h3 class="text-[11px] font-bold text-slate-400 uppercase tracking-widest">⚡ Actions</h3>
        <span class="h-px flex-1 bg-slate-200 ml-4"></span>
      </div>

      <div class="grid grid-cols-2 gap-2">
        <button @click="undo" :disabled="historyIndex <= 0"
                class="p-2 bg-white border border-slate-200 rounded-lg hover:border-indigo-400 disabled:opacity-30 disabled:cursor-not-allowed text-xs font-semibold text-slate-700 hover:text-indigo-600 transition-all">
          ↶ Annuler
        </button>
        <button @click="redo" :disabled="historyIndex >= historyStack.length - 1"
                class="p-2 bg-white border border-slate-200 rounded-lg hover:border-indigo-400 disabled:opacity-30 disabled:cursor-not-allowed text-xs font-semibold text-slate-700 hover:text-indigo-600 transition-all">
          ↷ Rétablir
        </button>
        <button @click="exportConfig"
                class="p-2 bg-white border border-slate-200 rounded-lg hover:border-green-400 text-xs font-semibold text-slate-700 hover:text-green-600 transition-all">
          📥 Export
        </button>
        <label class="p-2 bg-white border border-slate-200 rounded-lg hover:border-green-400 text-xs font-semibold text-slate-700 hover:text-green-600 transition-all cursor-pointer text-center">
          📤 Import
          <input type="file" accept=".json" @change="importConfig" class="hidden" />
        </label>
      </div>

      <!-- Bouton Page Vierge -->
      <button @click="resetToBlank"
              class="w-full p-3 bg-white border-2 border-orange-200 rounded-xl hover:border-orange-400 hover:bg-orange-50 text-xs font-black text-orange-600 uppercase tracking-wider transition-all active:scale-[0.98] flex items-center justify-center gap-2">
        <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
        </svg>
        🗑️ Page Vierge
      </button>
    </div>

    <div class="space-y-4">
      <div class="flex items-center justify-between px-2">
        <h3 class="text-[11px] font-bold text-slate-400 uppercase tracking-widest">Configuration</h3>
        <span class="h-px flex-1 bg-slate-200 ml-4"></span>
      </div>



      <!-- Résolution -->
      <div class="space-y-2">
        <label class="text-[10px] font-bold text-slate-500 ml-1 uppercase">Résolution de l'écran</label>
        <div class="grid grid-cols-2 gap-2">
          <div>
            <label class="text-[9px] text-slate-400 mb-1 block">Largeur (px)</label>
            <input type="number" v-model.number="config.targetResolution.width" min="640" max="7680"
                   class="w-full p-2 bg-white rounded-lg border border-slate-200 text-sm font-mono text-center focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500" />
          </div>
          <div>
            <label class="text-[9px] text-slate-400 mb-1 block">Hauteur (px)</label>
            <input type="number" v-model.number="config.targetResolution.height" min="480" max="4320"
                   class="w-full p-2 bg-white rounded-lg border border-slate-200 text-sm font-mono text-center focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500" />
          </div>
        </div>
        <div class="flex gap-2">
          <button @click="setResolution(1920, 1080)" class="flex-1 px-2 py-1.5 bg-slate-100 hover:bg-slate-200 rounded-lg text-[10px] font-bold text-slate-600 transition-all">1920×1080</button>
          <button @click="setResolution(1080, 1920)" class="flex-1 px-2 py-1.5 bg-slate-100 hover:bg-slate-200 rounded-lg text-[10px] font-bold text-slate-600 transition-all">1080×1920</button>
          <button @click="setResolution(3840, 2160)" class="flex-1 px-2 py-1.5 bg-slate-100 hover:bg-slate-200 rounded-lg text-[10px] font-bold text-slate-600 transition-all">4K</button>
        </div>
        <p class="text-[9px] text-slate-400 italic">{{ config.targetResolution.width }} × {{ config.targetResolution.height }} px</p>
      </div>

      <div class="group relative">
        <label class="text-[10px] font-bold text-slate-500 ml-1 uppercase mb-2 block">Layout des zones</label>
        <select v-model="config.layout.type"
                class="w-full appearance-none bg-white p-3 pr-10 rounded-xl border border-slate-200 text-sm font-medium text-slate-700 shadow-sm transition-all focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 outline-none cursor-pointer hover:border-slate-300">
          <option value="single">Simple (1 zone)</option>
          <option value="split_horizontal">Split Horizontal</option>
          <option value="split_vertical">Split Vertical</option>
          <option value="grid_2x2">Grille 2x2</option>
          <option value="custom">Personnalisé (Libre)</option>
        </select>
        <div class="absolute right-3 top-[38px] -translate-y-1/2 pointer-events-none text-slate-400">
          <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
          </svg>
        </div>
      </div>

      <div v-if="config.layout.type === 'custom'"
           class="p-3 bg-indigo-50/50 border border-indigo-100 rounded-xl animate-in fade-in slide-in-from-top-2">
        <p class="text-[11px] text-indigo-700 leading-relaxed font-medium">
          ✨ <strong>Mode libre :</strong> Ajustez les zones directement par drag & drop dans l'aperçu.
        </p>
      </div>

      <!-- Espacement de la page -->
      <div v-if="config.layout.type !== 'custom'" class="space-y-3">
        <label class="text-[10px] font-bold text-slate-500 ml-1 uppercase">Espacement de la page</label>

        <!-- Padding global -->
        <div>
          <label class="text-[9px] text-slate-400 mb-1.5 block flex items-center justify-between">
            <span>Padding (marge intérieure)</span>
            <span class="font-bold text-indigo-600">{{ config.layout.padding || 24 }}px</span>
          </label>
          <input type="range" v-model.number="config.layout.padding" min="0" max="100" step="4"
                 class="w-full h-2 bg-slate-200 rounded-lg appearance-none cursor-pointer accent-indigo-600" />
          <div class="flex gap-1 mt-1.5">
            <button @click="config.layout.padding = 0" class="flex-1 px-2 py-1 bg-slate-100 hover:bg-slate-200 rounded text-[9px] font-bold text-slate-600 transition-all">0</button>
            <button @click="config.layout.padding = 24" class="flex-1 px-2 py-1 bg-slate-100 hover:bg-slate-200 rounded text-[9px] font-bold text-slate-600 transition-all">24</button>
            <button @click="config.layout.padding = 48" class="flex-1 px-2 py-1 bg-slate-100 hover:bg-slate-200 rounded text-[9px] font-bold text-slate-600 transition-all">48</button>
          </div>
        </div>

        <!-- Gap entre zones -->
        <div>
          <label class="text-[9px] text-slate-400 mb-1.5 block flex items-center justify-between">
            <span>Gap (espace entre zones)</span>
            <span class="font-bold text-indigo-600">{{ config.layout.gap || 20 }}px</span>
          </label>
          <input type="range" v-model.number="config.layout.gap" min="0" max="100" step="4"
                 class="w-full h-2 bg-slate-200 rounded-lg appearance-none cursor-pointer accent-indigo-600" />
          <div class="flex gap-1 mt-1.5">
            <button @click="config.layout.gap = 0" class="flex-1 px-2 py-1 bg-slate-100 hover:bg-slate-200 rounded text-[9px] font-bold text-slate-600 transition-all">0</button>
            <button @click="config.layout.gap = 20" class="flex-1 px-2 py-1 bg-slate-100 hover:bg-slate-200 rounded text-[9px] font-bold text-slate-600 transition-all">20</button>
            <button @click="config.layout.gap = 40" class="flex-1 px-2 py-1 bg-slate-100 hover:bg-slate-200 rounded text-[9px] font-bold text-slate-600 transition-all">40</button>
          </div>
        </div>
      </div>
    </div>

    <div class="space-y-4">
      <div class="flex items-center justify-between px-2">
        <h3 class="text-[11px] font-bold text-slate-400 uppercase tracking-widest">Zones actives</h3>
        <span class="h-px flex-1 bg-slate-200 ml-4"></span>
      </div>

      <div class="space-y-2.5">
        <div v-for="(zone) in config.zones" :key="zone.id" class="relative">
          <button @click="selectedZone = zone"
                  :class="[
                    'w-full text-left p-4 rounded-xl transition-all duration-200 group relative overflow-hidden',
                    selectedZone?.id === zone.id
                      ? 'bg-white border-indigo-600 ring-1 ring-indigo-600 shadow-md shadow-indigo-100'
                      : 'bg-white border border-slate-200 hover:border-slate-300 hover:shadow-sm'
                  ]">
            <div class="flex items-center justify-between">
              <span :class="['font-bold text-sm', selectedZone?.id === zone.id ? 'text-indigo-600' : 'text-slate-700', !zone.name || zone.name.trim() === '' ? 'italic opacity-60' : '']">
                {{ zone.name && zone.name.trim() !== '' ? zone.name : '(Sans titre)' }}
              </span>
              <div v-if="selectedZone?.id === zone.id" class="w-1.5 h-1.5 rounded-full bg-indigo-600 animate-pulse"></div>
            </div>
            <div class="text-[10px] font-medium text-slate-400 mt-0.5 group-hover:text-slate-500 italic">
              {{ getContentTypeLabel(zone.content.type) }}
            </div>
          </button>

          <!-- Actions rapides sur zone -->
          <div v-if="selectedZone?.id === zone.id" class="flex gap-1 mt-2">
            <button @click="duplicateZone(zone)" class="flex-1 px-2 py-1.5 bg-indigo-50 border border-indigo-200 rounded-lg text-[10px] font-bold text-indigo-600 hover:bg-indigo-100 transition-all">
              📋 Dupliquer
            </button>
            <button @click="copyStyle(zone)" class="flex-1 px-2 py-1.5 bg-blue-50 border border-blue-200 rounded-lg text-[10px] font-bold text-blue-600 hover:bg-blue-100 transition-all">
              🎨 Copier style
            </button>
            <button @click="pasteStyle(zone)" :disabled="!copiedStyle" class="flex-1 px-2 py-1.5 bg-green-50 border border-green-200 rounded-lg text-[10px] font-bold text-green-600 hover:bg-green-100 transition-all disabled:opacity-30 disabled:cursor-not-allowed">
              ✨ Coller style
            </button>
          </div>
        </div>
      </div>

      <button @click="addZone" 
              class="w-full py-3 border-2 border-dashed border-slate-200 rounded-xl text-slate-500 text-[11px] font-black uppercase tracking-tighter hover:bg-white hover:border-indigo-400 hover:text-indigo-600 transition-all active:scale-[0.98]">
        + Nouvelle zone
      </button>
    </div>

  </div>

  <div class="p-6 bg-white border-t border-slate-100 shadow-[0_-10px_20px_-5px_rgba(0,0,0,0.05)]">
    <button @click="saveConfig" 
            class="w-full py-4 bg-slate-900 text-white rounded-xl font-bold text-sm shadow-xl shadow-slate-200 hover:bg-indigo-600 hover:-translate-y-0.5 active:translate-y-0 transition-all duration-300 flex items-center justify-center gap-3 group">
      <span>Publier les changements</span>
      <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 group-hover:translate-x-1 transition-transform" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M14 5l7 7m0 0l-7 7m7-7H3" />
      </svg>
    </button>
  </div>
</aside>

    <!-- Canvas Preview -->
    <main class="flex-1 relative bg-slate-100 overflow-hidden flex flex-col">
      <!-- Barre d'outils Preview -->
      <div class="bg-white border-b border-slate-200 px-6 py-3 flex items-center justify-between">
        <div class="flex items-center gap-4">
          <div class="flex items-center gap-2 bg-slate-50 rounded-lg p-1">
            <button @click="setZoom(zoomLevel - 10)" class="px-3 py-1.5 text-xs font-bold text-slate-600 hover:bg-white rounded transition-all">−</button>
            <span class="text-xs font-bold text-slate-700 min-w-[60px] text-center">{{ zoomLevel }}%</span>
            <button @click="setZoom(zoomLevel + 10)" class="px-3 py-1.5 text-xs font-bold text-slate-600 hover:bg-white rounded transition-all">+</button>
            <button @click="setZoom(100)" class="px-3 py-1.5 text-xs font-bold text-indigo-600 hover:bg-white rounded transition-all">Reset</button>
          </div>

          <div class="flex gap-2">
            <button @click="setZoom(50)" class="px-3 py-1.5 text-[10px] font-semibold text-slate-600 hover:bg-slate-100 rounded-lg transition-all">50%</button>
            <button @click="setZoom(75)" class="px-3 py-1.5 text-[10px] font-semibold text-slate-600 hover:bg-slate-100 rounded-lg transition-all">75%</button>
            <button @click="setZoom(100)" class="px-3 py-1.5 text-[10px] font-semibold text-slate-600 hover:bg-slate-100 rounded-lg transition-all">100%</button>
            <button @click="setZoom(150)" class="px-3 py-1.5 text-[10px] font-semibold text-slate-600 hover:bg-slate-100 rounded-lg transition-all">150%</button>
          </div>
        </div>

        <div class="flex items-center gap-2">
          <div class="flex items-center gap-3">
            <div class="text-xs text-slate-500 font-medium font-mono">{{ config.targetResolution.width }}×{{ config.targetResolution.height }}</div>
            <div class="px-2 py-1 rounded-md text-[10px] font-bold uppercase tracking-wide" :class="config.display.orientation === 'horizontal' ? 'bg-blue-50 text-blue-600' : 'bg-purple-50 text-purple-600'">
              {{ config.display.orientation === 'horizontal' ? '📺 Horizontal' : '📱 Vertical' }}
            </div>
            <div class="text-[10px] text-slate-400 font-medium">
              {{ getAspectRatio() }}
            </div>
          </div>
        </div>
      </div>

      <div class="flex-1 p-12 flex justify-center items-center overflow-auto">
        <div class="flex flex-col items-center gap-4">


          <!-- Preview Container -->
          <div class="relative bg-white shadow-2xl transition-all duration-300" :style="getPreviewContainerStyle()">
        <!-- Background -->
        <div class="absolute inset-0" :style="getBackgroundStyle()"></div>

        <!-- Grille de fond en mode custom -->
        <div v-if="config.layout.type === 'custom'" class="absolute inset-0 pointer-events-none" style="z-index: 1;">
          <svg class="w-full h-full opacity-10">
            <defs>
              <pattern id="grid" width="40" height="40" patternUnits="userSpaceOnUse">
                <path d="M 40 0 L 0 0 0 40" fill="none" stroke="currentColor" stroke-width="0.5" />
              </pattern>
            </defs>
            <rect width="100%" height="100%" fill="url(#grid)" />
          </svg>
        </div>

        <!-- Logo -->
        <div v-if="config?.branding?.logo?.show && config.branding.logo.url"
             :class="['absolute z-10', getLogoPositionClass()]"
             class="p-4">
          <img :src="config.branding.logo.url" :style="{ width: config.branding.logo.size + 'px', height: 'auto' }" />
        </div>

        <!-- Zones -->
        <div class="relative w-full h-full"
             :class="config.layout.type === 'custom' ? '' : 'flex ' + getLayoutClass()"
             :style="getZonesContainerStyle()">
          <div v-for="zone in config.zones" :key="zone.id"
               @click="selectedZone = zone"
               class="relative cursor-pointer transition-all group"
               :class="selectedZone?.id === zone.id ? 'ring-4 ring-blue-500 z-10' : 'hover:ring-2 hover:ring-blue-300'"
               :style="getZoneStyle(zone)">

            <!-- Label de la zone en mode custom -->
            <div v-if="config.layout.type === 'custom' && selectedZone?.id === zone.id" class="absolute -top-7 left-0 bg-blue-600 text-white px-2 py-1 rounded text-[10px] font-bold whitespace-nowrap z-20 shadow-lg">
              {{ zone.name && zone.name.trim() !== '' ? zone.name : '(Sans titre)' }} ({{ Math.round(zone.position.width) }}% × {{ Math.round(zone.position.height) }}%)
            </div>

            <!-- Handles de redimensionnement visibles sur sélection (custom mode) -->
            <div v-if="config.layout.type === 'custom' && selectedZone?.id === zone.id" class="absolute inset-0 pointer-events-none">
              <!-- Coins -->
              <div class="absolute -top-1 -left-1 w-3 h-3 bg-blue-500 rounded-full border-2 border-white shadow"></div>
              <div class="absolute -top-1 -right-1 w-3 h-3 bg-blue-500 rounded-full border-2 border-white shadow"></div>
              <div class="absolute -bottom-1 -left-1 w-3 h-3 bg-blue-500 rounded-full border-2 border-white shadow"></div>
              <div class="absolute -bottom-1 -right-1 w-3 h-3 bg-blue-500 rounded-full border-2 border-white shadow"></div>
            </div>

            <!-- Zone Content Preview -->
            <div class="w-full h-full overflow-hidden">
              <div v-if="zone.content.type === 'queue_display'" class="h-full flex flex-col" :style="getZoneContentPadding(zone)">

                <!-- Preview Tickets Appelés -->
                <div v-if="zone.content.ticketFilter === 'CALLED' || zone.content.ticketFilter === 'BOTH'" class="mb-4">
                  <div class="text-xs font-bold mb-3 uppercase tracking-wider opacity-70" :style="{ color: zone.textStyle.color }">
                    {{ zone.content.ticketFilter === 'CALLED' ? 'Appelés' : 'En cours' }}
                  </div>

                  <!-- Format Block Preview -->
                  <div v-if="!zone.content.calledTicketDisplayFormat || zone.content.calledTicketDisplayFormat === 'block'" class="rounded-xl border-2 border-blue-400/30 bg-blue-50/20" style="text-align: center; padding: 32px;">
                    <div v-if="zone.content.ticketDisplayInfo?.showNumber !== false" class="flex items-center justify-center" style="gap: 12px;" :style="{
                      fontFamily: zone.content.calledTicketStyle?.fontFamily || zone.textStyle?.fontFamily || 'sans-serif',
                      fontSize: (zone.content.calledTicketStyle?.fontSize || zone.textStyle?.fontSize || 32) + 'px',
                      fontWeight: '900',
                      color: zone.content.calledTicketStyle?.color || zone.textStyle?.color || '#FFFFFF'
                    }">
                      <PharmacyIcon name="prescription" :size="32" :color="zone.content.calledTicketStyle?.color || zone.textStyle?.color || '#FFFFFF'" />
                      V042
                    </div>
                    <div v-if="zone.content.ticketDisplayInfo?.showQueueName !== false" :style="{
                      fontSize: '16px',
                      marginTop: '8px',
                      opacity: 0.8,
                      color: zone.textStyle?.color || '#FFFFFF',
                      fontFamily: zone.textStyle?.fontFamily || 'sans-serif'
                    }">
                      Ordonnances
                    </div>
                    <div v-if="zone.content.ticketDisplayInfo?.showCounterName !== false" :style="{
                      fontSize: (zone.content.counterStyle?.fontSize || (zone.textStyle?.fontSize || 32) * 1.2) + 'px',
                      fontWeight: zone.content.counterStyle?.fontWeight || '700',
                      textTransform: 'uppercase',
                      color: zone.content.counterStyle?.color || zone.textStyle?.color || '#FFFFFF',
                      fontFamily: zone.content.counterStyle?.fontFamily || zone.textStyle?.fontFamily || 'sans-serif',
                      backgroundColor: zone.content.counterStyle?.backgroundColor || 'transparent',
                      padding: zone.content.counterStyle?.padding ? zone.content.counterStyle.padding + 'px' : '12px 24px',
                      borderRadius: zone.content.counterStyle?.borderRadius ? zone.content.counterStyle.borderRadius + 'px' : '12px',
                      borderWidth: zone.content.counterStyle?.borderWidth ? zone.content.counterStyle.borderWidth + 'px' : '2px',
                      borderStyle: 'solid',
                      borderColor: zone.content.counterStyle?.borderColor || '#1e3a8a',
                      marginTop: '12px',
                      display: 'inline-block'
                    }">Caisse 1</div>
                  </div>

                  <!-- Format Inline Preview -->
                  <div v-else-if="zone.content.calledTicketDisplayFormat === 'inline'" class="rounded-xl border-2 border-blue-400/30 bg-blue-50/20 flex items-center justify-center flex-wrap" style="padding: 24px 40px; gap: 20px;">
                    <div v-if="zone.content.ticketDisplayInfo?.showNumber !== false" class="flex items-center" style="gap: 12px;" :style="{
                      fontFamily: zone.content.calledTicketStyle?.fontFamily || zone.textStyle?.fontFamily || 'sans-serif',
                      fontSize: (zone.content.calledTicketStyle?.fontSize || zone.textStyle?.fontSize || 32) + 'px',
                      fontWeight: '900',
                      color: zone.content.calledTicketStyle?.color || zone.textStyle?.color || '#FFFFFF'
                    }">
                      <PharmacyIcon name="prescription" :size="28" :color="zone.content.calledTicketStyle?.color || zone.textStyle?.color || '#FFFFFF'" />
                      V042
                    </div>
                    <div v-if="zone.content.ticketDisplayInfo?.showCounterName !== false" :style="{ fontSize: '32px', fontWeight: '900', color: zone.textStyle?.color || '#FFFFFF', opacity: 0.3 }">
                      --------
                    </div>
                    <div v-if="zone.content.ticketDisplayInfo?.showCounterName !== false" :style="{
                      fontSize: (zone.content.counterStyle?.fontSize || (zone.textStyle?.fontSize || 32) * 1.2) + 'px',
                      fontWeight: zone.content.counterStyle?.fontWeight || '700',
                      textTransform: 'uppercase',
                      color: zone.content.counterStyle?.color || zone.textStyle?.color || '#FFFFFF',
                      fontFamily: zone.content.counterStyle?.fontFamily || zone.textStyle?.fontFamily || 'sans-serif',
                      backgroundColor: zone.content.counterStyle?.backgroundColor || 'transparent',
                      padding: zone.content.counterStyle?.padding ? zone.content.counterStyle.padding + 'px' : '12px 24px',
                      borderRadius: zone.content.counterStyle?.borderRadius ? zone.content.counterStyle.borderRadius + 'px' : '12px',
                      borderWidth: zone.content.counterStyle?.borderWidth ? zone.content.counterStyle.borderWidth + 'px' : '2px',
                      borderStyle: 'solid',
                      borderColor: zone.content.counterStyle?.borderColor || '#1e3a8a',
                      display: 'inline-block'
                    }">Caisse 1</div>
                    <div v-if="zone.content.ticketDisplayInfo?.showQueueName !== false" :style="{
                      fontSize: '18px',
                      fontWeight: '600',
                      color: zone.textStyle?.color || '#FFFFFF',
                      opacity: 0.7,
                      marginLeft: '10px',
                      fontFamily: zone.textStyle?.fontFamily || 'sans-serif'
                    }">
                      (Ordonnances)
                    </div>
                  </div>

                  <!-- Format Compact Preview -->
                  <div v-else-if="zone.content.calledTicketDisplayFormat === 'compact'" class="rounded-xl border-2 border-blue-400/30 bg-blue-50/20 flex items-center justify-between" style="padding: 20px 30px;">
                    <div class="flex flex-col">
                      <div v-if="zone.content.ticketDisplayInfo?.showNumber !== false" class="flex items-center" style="gap: 12px;" :style="{
                        fontFamily: zone.textStyle?.fontFamily || 'sans-serif',
                        fontSize: '48px',
                        fontWeight: '900',
                        color: zone.textStyle?.color || '#FFFFFF'
                      }">
                        <PharmacyIcon name="prescription" :size="24" :color="zone.textStyle?.color || '#FFFFFF'" />
                        V042
                      </div>
                      <div v-if="zone.content.ticketDisplayInfo?.showQueueName !== false" :style="{
                        fontSize: '14px',
                        fontWeight: '600',
                        color: zone.textStyle?.color || '#FFFFFF',
                        opacity: 0.6,
                        marginTop: '4px',
                        fontFamily: zone.textStyle?.fontFamily || 'sans-serif'
                      }">
                        Ordonnances
                      </div>
                    </div>
                    <div v-if="zone.content.ticketDisplayInfo?.showCounterName !== false" :style="{
                      fontSize: (zone.content.counterStyle?.fontSize || 28) + 'px',
                      fontWeight: zone.content.counterStyle?.fontWeight || '700',
                      textTransform: 'uppercase',
                      color: zone.content.counterStyle?.color || zone.textStyle?.color || '#FFFFFF',
                      fontFamily: zone.content.counterStyle?.fontFamily || zone.textStyle?.fontFamily || 'sans-serif',
                      backgroundColor: zone.content.counterStyle?.backgroundColor || 'transparent',
                      padding: zone.content.counterStyle?.padding ? zone.content.counterStyle.padding + 'px' : '12px 24px',
                      borderRadius: zone.content.counterStyle?.borderRadius ? zone.content.counterStyle.borderRadius + 'px' : '12px',
                      borderWidth: zone.content.counterStyle?.borderWidth ? zone.content.counterStyle.borderWidth + 'px' : '2px',
                      borderStyle: 'solid',
                      borderColor: zone.content.counterStyle?.borderColor || '#1e3a8a',
                      display: 'inline-block'
                    }">Caisse 1</div>
                  </div>
                </div>

                <!-- Preview Tickets En Attente -->
                <div v-if="zone.content.ticketFilter === 'WAITING' || zone.content.ticketFilter === 'BOTH'">
                  <div class="text-xs font-bold mb-3 uppercase tracking-wider opacity-70" :style="{ color: zone.textStyle.color }">
                    {{ zone.content.ticketFilter === 'WAITING' ? 'File d\'attente' : 'En attente' }}
                  </div>
                  <div class="grid gap-2" :style="{ gridTemplateColumns: `repeat(${zone.content.gridColumns || 2}, 1fr)` }">
                    <div v-for="i in Math.min(zone.content.maxTickets || 10, 6)" :key="i"
                         class="flex flex-col items-center justify-center"
                         :style="{
                           backgroundColor: zone.content.waitingTicketStyle.backgroundColor,
                           padding: zone.content.waitingTicketStyle.padding + 'px',
                           borderRadius: zone.content.waitingTicketStyle.borderRadius + 'px'
                         }">
                      <div v-if="zone.content.ticketDisplayInfo?.showNumber !== false" class="font-black flex items-center justify-center gap-2" :style="{
                        fontFamily: zone.content.waitingTicketStyle.fontFamily,
                        fontSize: zone.content.waitingTicketStyle.fontSize + 'px',
                        color: zone.content.waitingTicketStyle.color,
                        textShadow: zone.style.textShadow?.enabled ? getTextShadowValue(zone.style.textShadow) : 'none'
                      }">
                        <PharmacyIcon name="prescription" :size="14" :color="zone.content.waitingTicketStyle.color" />
                        A{{ String(i).padStart(3, '0') }}
                      </div>
                      <div v-if="zone.content.ticketDisplayInfo?.showQueueName !== false" class="text-[8px] uppercase tracking-wider opacity-60 mt-1" :style="{ color: zone.content.waitingTicketStyle.color }">
                        Ordonnances
                      </div>
                      <div v-if="zone.content.ticketDisplayInfo?.showWaitTime === true" class="text-[7px] font-bold opacity-50 mt-1" :style="{ color: zone.content.waitingTicketStyle.color }">
                        {{ i * 2 }} min
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Preview Historique -->
                <div v-if="zone.content.ticketFilter === 'HISTORY'">
                  <div class="text-xs font-bold mb-3 uppercase tracking-wider opacity-70" :style="{ color: zone.textStyle.color }">
                    Historique
                  </div>
                  <div class="grid gap-2" :style="{ gridTemplateColumns: `repeat(${zone.content.gridColumns || 3}, 1fr)` }">
                    <div v-for="i in Math.min(zone.content.maxTickets || 12, 6)" :key="i"
                         class="flex flex-col items-center justify-center opacity-60"
                         :style="{
                           backgroundColor: zone.content.historyTicketStyle?.backgroundColor || zone.content.waitingTicketStyle.backgroundColor,
                           padding: (zone.content.historyTicketStyle?.padding || zone.content.waitingTicketStyle.padding) + 'px',
                           borderRadius: (zone.content.historyTicketStyle?.borderRadius || zone.content.waitingTicketStyle.borderRadius) + 'px'
                         }">
                      <div v-if="zone.content.ticketDisplayInfo?.showNumber !== false" class="font-black flex items-center justify-center gap-2" :style="{
                        fontFamily: zone.content.historyTicketStyle?.fontFamily || zone.content.waitingTicketStyle.fontFamily,
                        fontSize: (zone.content.historyTicketStyle?.fontSize || zone.content.waitingTicketStyle.fontSize) + 'px',
                        color: zone.content.historyTicketStyle?.color || zone.content.waitingTicketStyle.color,
                        textShadow: zone.style.textShadow?.enabled ? getTextShadowValue(zone.style.textShadow) : 'none'
                      }">
                        <PharmacyIcon name="prescription" :size="14" :color="zone.content.historyTicketStyle?.color || zone.content.waitingTicketStyle.color" />
                        V{{ String(42 - i).padStart(3, '0') }}
                      </div>
                      <div v-if="zone.content.ticketDisplayInfo?.showQueueName !== false" class="text-[8px] uppercase tracking-wider opacity-60 mt-1" :style="{ color: zone.content.historyTicketStyle?.color || zone.content.waitingTicketStyle.color }">
                        Ordonnances
                      </div>
                      <div v-if="zone.content.ticketDisplayInfo?.showWaitTime === true" class="text-[7px] font-bold opacity-50 mt-1" :style="{ color: zone.content.historyTicketStyle?.color || zone.content.waitingTicketStyle.color }">
                        Terminé
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div v-else-if="zone.content.type === 'video'" class="w-full h-full bg-black flex items-center justify-center text-white text-xs">
                🎬 Vidéo
              </div>

              <div v-else-if="zone.content.type === 'image'" class="w-full h-full">
                <img v-if="zone.content.imageUrl" :src="zone.content.imageUrl" class="w-full h-full object-cover" />
                <div v-else class="w-full h-full bg-slate-100 flex items-center justify-center text-slate-400 text-xs">
                  🖼️ Image
                </div>
              </div>

              <div v-else-if="zone.content.type === 'text'" :style="{
                ...zone.textStyle,
                fontSize: zone.textStyle.fontSize + 'px',
                textShadow: zone.style.textShadow?.enabled ? getTextShadowValue(zone.style.textShadow) : 'none'
              }">
                {{ zone.content.text || 'Votre texte ici' }}
              </div>

              <div v-else-if="zone.content.type === 'clock'" class="flex flex-col items-center justify-center h-full">
                <div class="text-4xl font-bold" :style="{
                  color: zone.textStyle.color,
                  textShadow: zone.style.textShadow?.enabled ? getTextShadowValue(zone.style.textShadow) : 'none'
                }">{{ currentTime }}</div>
                <div v-if="zone.content.showDate" class="text-sm mt-2" :style="{
                  color: zone.textStyle.color,
                  textShadow: zone.style.textShadow?.enabled ? getTextShadowValue(zone.style.textShadow) : 'none'
                }">{{ currentDate }}</div>
              </div>

              <div v-else-if="zone.content.type === 'weather'" class="flex items-center justify-center h-full">
                <div class="text-center">
                  <div class="text-5xl mb-2">☀️</div>
                  <div class="text-2xl font-bold" :style="{
                    color: zone.textStyle.color,
                    textShadow: zone.style.textShadow?.enabled ? getTextShadowValue(zone.style.textShadow) : 'none'
                  }">24°C</div>
                  <div class="text-xs mt-1" :style="{
                    color: zone.textStyle.color,
                    textShadow: zone.style.textShadow?.enabled ? getTextShadowValue(zone.style.textShadow) : 'none'
                  }">{{ zone.content.location || 'Paris' }}</div>
                </div>
              </div>

              <div v-else class="w-full h-full bg-slate-100 flex items-center justify-center text-slate-400 text-xs">
                Contenu personnalisé
              </div>
            </div>

            <!-- Zone Label -->
            <div class="absolute top-2 left-2 bg-blue-500 text-white text-[8px] px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity">
              {{ zone.name && zone.name.trim() !== '' ? zone.name : '(Sans titre)' }}
            </div>
          </div>
        </div>
          </div>
        </div>
      </div>
    </main>

    <!-- Sidebar Droite - Propriétés -->
    <aside class="w-80 bg-white border-l border-slate-200 flex flex-col z-20 shadow-xl overflow-hidden">
      <div class="p-6 border-b border-slate-100 bg-blue-50/50 flex justify-between items-center">
        <h2 class="font-bold text-slate-800 uppercase text-xs">
          {{ selectedZone ? (selectedZone.name && selectedZone.name.trim() !== '' ? selectedZone.name : '(Sans titre)') : 'Branding' }}
        </h2>
        <button v-if="selectedZone" @click="selectedZone = null" class="text-slate-400 hover:text-slate-600">✕</button>
      </div>

      <div class="flex-1 overflow-y-auto p-6 space-y-6">
        <!-- Zone Properties -->
        <template v-if="selectedZone">
          <!-- Nom de la zone -->
          <div class="space-y-2">
            <label class="text-[10px] font-black text-slate-400 uppercase">Nom de la zone <span class="text-[9px] text-slate-400 font-normal">(optionnel)</span></label>
            <input type="text" v-model="selectedZone.name" placeholder="Laisser vide pour ne pas afficher de titre" class="w-full p-2 bg-slate-50 rounded-lg border text-xs" />
            <p class="text-[8px] text-slate-400">Si vide, aucun titre ne sera affiché au-dessus de la zone (gagne de la place)</p>
          </div>

          <!-- Type de contenu -->
          <div class="space-y-2 pt-4 border-t">
            <label class="text-[10px] font-black text-slate-400 uppercase">Type de contenu</label>
            <select v-model="selectedZone.content.type" @change="onContentTypeChange" class="w-full p-2 bg-slate-50 rounded-lg border text-xs">
              <option value="queue_display">Affichage files d'attente</option>
              <option value="video">Vidéo</option>
              <option value="image">Image</option>
              <option value="text">Texte</option>
              <option value="clock">Horloge</option>
              <option value="weather">Météo</option>
              <option value="custom_html">HTML Personnalisé</option>
            </select>
          </div>

          <!-- Content specific options -->
          <div v-if="selectedZone.content.type === 'queue_display'" class="space-y-3 pt-4 border-t">
            <div class="space-y-2">
              <label class="text-[10px] font-black text-slate-400 uppercase">Files à afficher</label>
              <div class="space-y-1">
                <div v-for="queue in queues" :key="queue._id" class="flex items-center gap-2">
                  <input type="checkbox" :id="'queue-' + queue._id" :value="queue._id"
                         :checked="selectedZone.content.queueIds?.includes(queue._id)"
                         @change="toggleQueue(queue._id)" />
                  <label :for="'queue-' + queue._id" class="text-xs">{{ queue.prefix }} - {{ queue.name }}</label>
                </div>
              </div>
            </div>

            <div class="space-y-2">
              <label class="text-[10px] font-black text-slate-400 uppercase">Afficher les tickets</label>
              <select v-model="selectedZone.content.ticketFilter" class="w-full p-2 bg-slate-50 rounded-lg border text-xs">
                <option value="BOTH">En cours + En attente</option>
                <option value="CALLED">En cours seulement</option>
                <option value="WAITING">En attente seulement</option>
                <option value="HISTORY">Historique (Terminés)</option>
              </select>
              <p class="text-[9px] text-slate-500">
                <span v-if="selectedZone.content.ticketFilter === 'CALLED'">Affiche uniquement les tickets actuellement appelés</span>
                <span v-else-if="selectedZone.content.ticketFilter === 'WAITING'">Affiche uniquement les tickets en file d'attente</span>
                <span v-else-if="selectedZone.content.ticketFilter === 'HISTORY'">Affiche l'historique des tickets terminés (DONE/SKIPPED)</span>
                <span v-else>Affiche tous les tickets (appelés et en attente)</span>
              </p>
            </div>

            <div class="grid grid-cols-2 gap-2">
              <div>
                <label class="text-[10px] font-black text-slate-400 uppercase">Max tickets</label>
                <input type="number" v-model.number="selectedZone.content.maxTickets" min="1" max="50" class="w-full p-2 bg-slate-50 rounded-lg border text-xs" />
              </div>
              <div>
                <label class="text-[10px] font-black text-slate-400 uppercase">Colonnes</label>
                <input type="number" v-model.number="selectedZone.content.gridColumns" min="1" max="6" class="w-full p-2 bg-slate-50 rounded-lg border text-xs" />
              </div>
            </div>

            <!-- Format d'affichage des tickets appelés -->
            <div class="space-y-3 pt-3 border-t" v-if="selectedZone.content.ticketFilter === 'CALLED' || selectedZone.content.ticketFilter === 'BOTH'">
              <label class="text-[10px] font-black text-slate-400 uppercase">Format ticket appelé</label>

              <div>
                <label class="text-[9px] text-slate-500 mb-1 block">Nombre max de tickets appelés affichés</label>
                <input type="number" v-model.number="selectedZone.content.maxCalledTickets" min="1" max="20" class="w-full p-2 bg-slate-50 rounded-lg border text-xs" />
                <p class="text-[8px] text-slate-400 mt-1">Les tickets au-delà de cette limite passeront automatiquement en historique (DONE)</p>
              </div>

              <div>
                <label class="text-[9px] text-slate-500 mb-1 block">Style d'affichage</label>
                <select v-model="selectedZone.content.calledTicketDisplayFormat" class="w-full p-2 bg-slate-50 rounded-lg border text-xs font-bold">
                  <option value="block">📦 Block (Vertical)</option>
                  <option value="inline">➡️ Inline (V001 -------- Caisse A)</option>
                  <option value="compact">🔲 Compact</option>
                </select>
                <p class="text-[8px] text-slate-400 mt-1">Format d'affichage du ticket en cours d'appel</p>
              </div>
            </div>

            <!-- Informations à afficher sur les tickets -->
            <div class="space-y-3 pt-3 border-t">
              <label class="text-[10px] font-black text-slate-400 uppercase">Informations affichées</label>
              <div class="space-y-2">
                <label class="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" v-model="selectedZone.content.ticketDisplayInfo.showNumber" class="rounded border-slate-300 text-blue-600 focus:ring-blue-500" />
                  <span class="text-[10px] text-slate-700 font-semibold">Numéro du ticket</span>
                </label>
                <label class="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" v-model="selectedZone.content.ticketDisplayInfo.showQueueName" class="rounded border-slate-300 text-blue-600 focus:ring-blue-500" />
                  <span class="text-[10px] text-slate-700 font-semibold">Nom du service</span>
                </label>
                <label class="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" v-model="selectedZone.content.ticketDisplayInfo.showCounterName" class="rounded border-slate-300 text-blue-600 focus:ring-blue-500" />
                  <span class="text-[10px] text-slate-700 font-semibold">Nom du guichet/caisse</span>
                </label>
                <label class="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" v-model="selectedZone.content.ticketDisplayInfo.showWaitTime" class="rounded border-slate-300 text-blue-600 focus:ring-blue-500" />
                  <span class="text-[10px] text-slate-700 font-semibold">Temps d'attente</span>
                </label>
              </div>
              <p class="text-[8px] text-slate-400 mt-1">Choisissez les informations visibles sur chaque ticket</p>
            </div>

            <!-- Style du guichet/comptoir -->
            <div v-if="(selectedZone.content.ticketFilter === 'CALLED' || selectedZone.content.ticketFilter === 'BOTH') && selectedZone.content.counterStyle" class="space-y-3 pt-3 border-t">
              <label class="text-[10px] font-black text-slate-400 uppercase">Style du guichet/comptoir</label>
              <p class="text-[8px] text-slate-400">Personnalisez l'apparence du nom du guichet pour les tickets appelés</p>

              <div>
                <label class="text-[9px] text-slate-500 mb-1 block">Police</label>
                <select v-model="selectedZone.content.counterStyle.fontFamily" class="w-full p-2 bg-slate-50 rounded-lg border text-xs" :style="{ fontFamily: selectedZone.content.counterStyle.fontFamily }">
                  <option v-for="font in availableFonts" :key="font" :value="font" :style="{ fontFamily: font }">
                    {{ font }}
                  </option>
                </select>
              </div>

              <div class="grid grid-cols-2 gap-2">
                <div>
                  <label class="text-[9px] text-slate-500 mb-1 block">Taille texte</label>
                  <input type="number" v-model.number="selectedZone.content.counterStyle.fontSize" min="12" max="120" class="w-full p-2 bg-slate-50 rounded-lg border text-xs" />
                </div>
                <div>
                  <label class="text-[9px] text-slate-500 mb-1 block">Graisse</label>
                  <select v-model="selectedZone.content.counterStyle.fontWeight" class="w-full p-2 bg-slate-50 rounded-lg border text-xs">
                    <option value="400">Normal</option>
                    <option value="600">Semi-Bold</option>
                    <option value="700">Bold</option>
                    <option value="900">Black</option>
                  </select>
                </div>
              </div>

              <div class="grid grid-cols-2 gap-2">
                <div>
                  <label class="text-[9px] text-slate-500 mb-1 block">Couleur texte</label>
                  <input type="color" v-model="selectedZone.content.counterStyle.color" class="w-full h-9 rounded cursor-pointer" />
                </div>
                <div>
                  <label class="text-[9px] text-slate-500 mb-1 block">Couleur fond</label>
                  <input type="color" v-model="selectedZone.content.counterStyle.backgroundColor" class="w-full h-9 rounded cursor-pointer" />
                </div>
              </div>

              <div class="grid grid-cols-2 gap-2">
                <div>
                  <label class="text-[9px] text-slate-500 mb-1 block">Padding (px)</label>
                  <input type="number" v-model.number="selectedZone.content.counterStyle.padding" min="0" max="60" class="w-full p-2 bg-slate-50 rounded-lg border text-xs" />
                </div>
                <div>
                  <label class="text-[9px] text-slate-500 mb-1 block">Arrondi (px)</label>
                  <input type="number" v-model.number="selectedZone.content.counterStyle.borderRadius" min="0" max="50" class="w-full p-2 bg-slate-50 rounded-lg border text-xs" />
                </div>
              </div>

              <div class="grid grid-cols-2 gap-2">
                <div>
                  <label class="text-[9px] text-slate-500 mb-1 block">Épaisseur bordure (px)</label>
                  <input type="number" v-model.number="selectedZone.content.counterStyle.borderWidth" min="0" max="10" class="w-full p-2 bg-slate-50 rounded-lg border text-xs" />
                </div>
                <div>
                  <label class="text-[9px] text-slate-500 mb-1 block">Couleur bordure</label>
                  <input type="color" v-model="selectedZone.content.counterStyle.borderColor" class="w-full h-9 rounded cursor-pointer" />
                </div>
              </div>

              <button @click="resetCounterStyle" class="w-full px-3 py-2 text-[10px] text-blue-600 hover:text-blue-700 border border-blue-200 hover:border-blue-300 rounded-lg transition-colors">
                Réinitialiser le style du guichet
              </button>
            </div>

            <!-- Style des tickets appelés (En cours) -->
            <div v-if="(selectedZone.content.ticketFilter === 'CALLED' || selectedZone.content.ticketFilter === 'BOTH') && selectedZone.content.calledTicketStyle" class="space-y-3 pt-3 border-t">
              <label class="text-[10px] font-black text-slate-400 uppercase">Design des tickets appelés (En cours)</label>

              <div>
                <label class="text-[9px] text-slate-500 mb-1 block">Police</label>
                <select v-model="selectedZone.content.calledTicketStyle.fontFamily" class="w-full p-2 bg-slate-50 rounded-lg border text-xs" :style="{ fontFamily: selectedZone.content.calledTicketStyle.fontFamily }" @change="onCalledStyleChange">
                  <option v-for="font in availableFonts" :key="font" :value="font" :style="{ fontFamily: font }">
                    {{ font }}
                  </option>
                </select>
              </div>

              <div class="grid grid-cols-2 gap-2">
                <div>
                  <label class="text-[9px] text-slate-500 mb-1 block">Taille du numéro</label>
                  <input type="number" v-model.number="selectedZone.content.calledTicketStyle.fontSize" min="12" max="120" class="w-full p-2 bg-slate-50 rounded-lg border text-xs" @input="onCalledStyleChange" />
                </div>
                <div>
                  <label class="text-[9px] text-slate-500 mb-1 block">Couleur</label>
                  <input type="color" v-model="selectedZone.content.calledTicketStyle.color" class="w-full h-9 rounded cursor-pointer" @input="onCalledStyleChange" />
                </div>
              </div>

              <!-- Debug info -->
              <div class="text-[8px] text-slate-400 mt-2 p-2 bg-slate-50 rounded">
                Debug: fontSize={{ selectedZone.content.calledTicketStyle.fontSize }}, color={{ selectedZone.content.calledTicketStyle.color }}
              </div>
            </div>

            <!-- Style des tickets en attente (grille) -->
            <div v-if="selectedZone.content.ticketFilter === 'WAITING' || selectedZone.content.ticketFilter === 'BOTH'" class="space-y-3 pt-3 border-t">
              <label class="text-[10px] font-black text-slate-400 uppercase">Design des numéros en attente (grille)</label>

              <div>
                <label class="text-[9px] text-slate-500 mb-1 block">Police</label>
                <select v-model="selectedZone.content.waitingTicketStyle.fontFamily" class="w-full p-2 bg-slate-50 rounded-lg border text-xs" :style="{ fontFamily: selectedZone.content.waitingTicketStyle.fontFamily }">
                  <option v-for="font in availableFonts" :key="font" :value="font" :style="{ fontFamily: font }">
                    {{ font }}
                  </option>
                </select>
              </div>

              <div class="grid grid-cols-2 gap-2">
                <div>
                  <label class="text-[9px] text-slate-500 mb-1 block">Taille texte</label>
                  <input type="number" v-model.number="selectedZone.content.waitingTicketStyle.fontSize" min="12" max="80" class="w-full p-2 bg-slate-50 rounded-lg border text-xs" />
                </div>
                <div>
                  <label class="text-[9px] text-slate-500 mb-1 block">Couleur texte</label>
                  <input type="color" v-model="selectedZone.content.waitingTicketStyle.color" class="w-full h-9 rounded cursor-pointer" />
                </div>
              </div>

              <div>
                <label class="text-[9px] text-slate-500 mb-1 block">Fond des cartes</label>
                <div class="flex gap-2">
                  <input type="text" v-model="selectedZone.content.waitingTicketStyle.backgroundColor" placeholder="rgba(255,255,255,0.1)" class="flex-1 p-2 bg-slate-50 rounded-lg border text-xs font-mono" />
                  <button @click="selectedZone.content.waitingTicketStyle.backgroundColor = 'rgba(255, 255, 255, 0.1)'" class="px-2 text-[9px] text-blue-500 hover:text-blue-600 border rounded">Reset</button>
                </div>
                <p class="text-[8px] text-slate-400 mt-1">Format: rgba(r,g,b,a) ou #hex</p>
              </div>

              <div class="grid grid-cols-2 gap-2">
                <div>
                  <label class="text-[9px] text-slate-500 mb-1 block">Padding (px)</label>
                  <input type="number" v-model.number="selectedZone.content.waitingTicketStyle.padding" min="0" max="40" class="w-full p-2 bg-slate-50 rounded-lg border text-xs" />
                </div>
                <div>
                  <label class="text-[9px] text-slate-500 mb-1 block">Arrondi (px)</label>
                  <input type="number" v-model.number="selectedZone.content.waitingTicketStyle.borderRadius" min="0" max="40" class="w-full p-2 bg-slate-50 rounded-lg border text-xs" />
                </div>
              </div>
            </div>

            <!-- Style des tickets d'historique -->
            <div v-if="selectedZone.content.ticketFilter === 'HISTORY'" class="space-y-3 pt-3 border-t">
              <label class="text-[10px] font-black text-slate-400 uppercase">Design de l'historique</label>

              <div>
                <label class="text-[9px] text-slate-500 mb-1 block">Police</label>
                <select v-model="selectedZone.content.historyTicketStyle.fontFamily" class="w-full p-2 bg-slate-50 rounded-lg border text-xs" :style="{ fontFamily: selectedZone.content.historyTicketStyle.fontFamily }">
                  <option v-for="font in availableFonts" :key="font" :value="font" :style="{ fontFamily: font }">
                    {{ font }}
                  </option>
                </select>
              </div>

              <div class="grid grid-cols-2 gap-2">
                <div>
                  <label class="text-[9px] text-slate-500 mb-1 block">Taille texte</label>
                  <input type="number" v-model.number="selectedZone.content.historyTicketStyle.fontSize" min="12" max="80" class="w-full p-2 bg-slate-50 rounded-lg border text-xs" />
                </div>
                <div>
                  <label class="text-[9px] text-slate-500 mb-1 block">Couleur texte</label>
                  <input type="color" v-model="selectedZone.content.historyTicketStyle.color" class="w-full h-9 rounded cursor-pointer" />
                </div>
              </div>

              <div>
                <label class="text-[9px] text-slate-500 mb-1 block">Fond des cartes</label>
                <div class="flex gap-2">
                  <input type="text" v-model="selectedZone.content.historyTicketStyle.backgroundColor" placeholder="rgba(200,200,200,0.2)" class="flex-1 p-2 bg-slate-50 rounded-lg border text-xs font-mono" />
                  <button @click="selectedZone.content.historyTicketStyle.backgroundColor = 'rgba(200, 200, 200, 0.2)'" class="px-2 text-[9px] text-blue-500 hover:text-blue-600 border rounded">Reset</button>
                </div>
                <p class="text-[8px] text-slate-400 mt-1">Format: rgba(r,g,b,a) ou #hex</p>
              </div>

              <div class="grid grid-cols-2 gap-2">
                <div>
                  <label class="text-[9px] text-slate-500 mb-1 block">Padding (px)</label>
                  <input type="number" v-model.number="selectedZone.content.historyTicketStyle.padding" min="0" max="40" class="w-full p-2 bg-slate-50 rounded-lg border text-xs" />
                </div>
                <div>
                  <label class="text-[9px] text-slate-500 mb-1 block">Arrondi (px)</label>
                  <input type="number" v-model.number="selectedZone.content.historyTicketStyle.borderRadius" min="0" max="40" class="w-full p-2 bg-slate-50 rounded-lg border text-xs" />
                </div>
              </div>

              <div class="grid grid-cols-2 gap-2 mt-3">
                <div>
                  <label class="text-[9px] text-slate-500 mb-1 block">Opacité globale</label>
                  <input type="range" v-model.number="selectedZone.content.historyTicketStyle.opacity" min="0.3" max="1" step="0.1" class="w-full" />
                  <p class="text-[8px] text-slate-400 text-center">{{ (selectedZone.content.historyTicketStyle.opacity * 100).toFixed(0) }}%</p>
                </div>
                <div>
                  <label class="text-[9px] text-slate-500 mb-1 block">Tickets max</label>
                  <input type="number" v-model.number="selectedZone.content.maxTickets" min="1" max="100" class="w-full p-2 bg-slate-50 rounded-lg border text-xs" />
                </div>
              </div>

              <p class="text-[8px] text-slate-400 mt-2">
                💡 L'historique affiche les derniers tickets terminés (statut DONE ou SKIPPED)
              </p>
            </div>
          </div>

          <div v-if="selectedZone.content.type === 'video'" class="space-y-2 pt-4 border-t">
            <label class="text-[10px] text-slate-500 mb-1 block">URL Vidéo</label>
            <input type="text" v-model="selectedZone.content.videoUrl" placeholder="https://..." class="w-full p-2 bg-slate-50 rounded-lg border text-xs" />
            <div class="flex items-center gap-4">
              <label class="flex items-center gap-2 text-xs">
                <input type="checkbox" v-model="selectedZone.content.autoplay" />
                Lecture auto
              </label>
              <label class="flex items-center gap-2 text-xs">
                <input type="checkbox" v-model="selectedZone.content.loop" />
                Boucle
              </label>
            </div>
          </div>

          <div v-if="selectedZone.content.type === 'image'" class="space-y-2 pt-4 border-t">
            <label class="text-[10px] text-slate-500 mb-1 block">URL Image</label>
            <MediaPicker
              v-model="selectedZone.content.imageUrl"
              placeholder="URL de l'image"
              button-text="Choisir une image"
              :agency-id="agencyId"
            />
          </div>

          <div v-if="selectedZone.content.type === 'text'" class="space-y-2 pt-4 border-t">
            <label class="text-[10px] text-slate-500 mb-1 block">Texte</label>
            <textarea v-model="selectedZone.content.text" rows="4" class="w-full p-2 bg-slate-50 rounded-lg border text-xs"></textarea>
          </div>

          <div v-if="selectedZone.content.type === 'weather'" class="space-y-2 pt-4 border-t">
            <label class="text-[10px] text-slate-500 mb-1 block">Ville</label>
            <input type="text" v-model="selectedZone.content.location" placeholder="Paris" class="w-full p-2 bg-slate-50 rounded-lg border text-xs" />
          </div>

          <!-- Position et Taille (en mode custom) -->
          <div v-if="config.layout.type === 'custom'" class="space-y-3 pt-4 border-t">
            <label class="text-[10px] font-black text-slate-400 uppercase">Position & Taille</label>
            <div class="grid grid-cols-2 gap-2">
              <div>
                <label class="text-[9px] text-slate-500 mb-1 block">X (%)</label>
                <input type="number" v-model.number="selectedZone.position.x" min="0" max="100" class="w-full p-2 bg-slate-50 rounded-lg border text-xs" />
              </div>
              <div>
                <label class="text-[9px] text-slate-500 mb-1 block">Y (%)</label>
                <input type="number" v-model.number="selectedZone.position.y" min="0" max="100" class="w-full p-2 bg-slate-50 rounded-lg border text-xs" />
              </div>
              <div>
                <label class="text-[9px] text-slate-500 mb-1 block">Largeur (%)</label>
                <input type="number" v-model.number="selectedZone.position.width" min="1" max="100" class="w-full p-2 bg-slate-50 rounded-lg border text-xs" />
              </div>
              <div>
                <label class="text-[9px] text-slate-500 mb-1 block">Hauteur (%)</label>
                <input type="number" v-model.number="selectedZone.position.height" min="1" max="100" class="w-full p-2 bg-slate-50 rounded-lg border text-xs" />
              </div>
            </div>

            <!-- Bouton pour centrer la zone horizontalement -->
            <button
              @click="centerZone(selectedZone)"
              class="w-full mt-2 py-2 px-3 bg-blue-500 hover:bg-blue-600 text-white text-xs font-semibold rounded-lg transition-colors flex items-center justify-center gap-2"
            >
              <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 12h8M4 12h.01M19.99 12H20" />
              </svg>
              Centrer horizontalement
            </button>
          </div>

          <!-- Style Background -->
          <div class="space-y-3 pt-4 border-t">
            <label class="text-[10px] font-black text-slate-400 uppercase">Fond de zone</label>
            <select v-model="selectedZone.style.backgroundType" class="w-full p-2 bg-slate-50 rounded-lg border text-xs">
              <option value="color">Couleur unie</option>
              <option value="gradient">Dégradé</option>
              <option value="image">Image</option>
            </select>

            <div v-if="selectedZone.style.backgroundType === 'color'">
              <input type="color" v-model="selectedZone.style.backgroundColor" class="w-full h-10 rounded cursor-pointer" />
            </div>

            <div v-else-if="selectedZone.style.backgroundType === 'gradient'" class="space-y-2">
              <div class="grid grid-cols-2 gap-2">
                <div>
                  <label class="text-[9px] text-slate-500 mb-1 block">Couleur 1</label>
                  <input type="color" v-model="selectedZone.style.gradient.color1" class="w-full h-10 rounded cursor-pointer" />
                </div>
                <div>
                  <label class="text-[9px] text-slate-500 mb-1 block">Couleur 2</label>
                  <input type="color" v-model="selectedZone.style.gradient.color2" class="w-full h-10 rounded cursor-pointer" />
                </div>
              </div>
              <div>
                <label class="text-[9px] text-slate-500 mb-1 block">Direction</label>
                <select v-model="selectedZone.style.gradient.direction" class="w-full p-2 bg-slate-50 rounded-lg border text-xs">
                  <option value="to right">Horizontal (gauche → droite)</option>
                  <option value="to left">Horizontal (droite → gauche)</option>
                  <option value="to bottom">Vertical (haut → bas)</option>
                  <option value="to top">Vertical (bas → haut)</option>
                  <option value="135deg">Diagonal ↘</option>
                  <option value="45deg">Diagonal ↗</option>
                </select>
              </div>
            </div>

            <div v-else-if="selectedZone.style.backgroundType === 'image'" class="space-y-2">
              <label class="text-[9px] text-slate-500 mb-1 block">URL Image de fond</label>
              <MediaPicker
                v-model="selectedZone.style.backgroundImage"
                placeholder="URL de l'image de fond"
                button-text="Choisir l'image de fond"
                :agency-id="agencyId"
              />
            </div>
          </div>

          <!-- BORDURES -->
          <div class="space-y-3 pt-4 border-t">
            <label class="text-[10px] font-black text-slate-400 uppercase">Bordures</label>

            <div class="grid grid-cols-2 gap-2">
              <div>
                <label class="text-[9px] text-slate-500 mb-1 block">Épaisseur (px)</label>
                <input type="number" v-model.number="selectedZone.style.borderWidth" min="0" max="20" class="w-full p-2 bg-slate-50 rounded-lg border text-xs" />
              </div>
              <div>
                <label class="text-[9px] text-slate-500 mb-1 block">Style</label>
                <select v-model="selectedZone.style.borderStyle" class="w-full p-2 bg-slate-50 rounded-lg border text-xs">
                  <option value="solid">Solide</option>
                  <option value="dashed">Tirets</option>
                  <option value="dotted">Pointillés</option>
                </select>
              </div>
            </div>

            <div>
              <label class="text-[9px] text-slate-500 mb-1 block">Couleur</label>
              <input type="color" v-model="selectedZone.style.borderColor" class="w-full h-9 rounded cursor-pointer" />
            </div>

            <div>
              <label class="text-[9px] text-slate-500 mb-2 block">Coins arrondis (px)</label>
              <div class="grid grid-cols-2 gap-2">
                <div>
                  <label class="text-[8px] text-slate-400 mb-1 block">Haut gauche</label>
                  <input type="number" v-model.number="selectedZone.style.borderRadiusTopLeft" :placeholder="selectedZone.style.borderRadius || 0" min="0" class="w-full p-2 bg-slate-50 rounded-lg border text-xs" />
                </div>
                <div>
                  <label class="text-[8px] text-slate-400 mb-1 block">Haut droite</label>
                  <input type="number" v-model.number="selectedZone.style.borderRadiusTopRight" :placeholder="selectedZone.style.borderRadius || 0" min="0" class="w-full p-2 bg-slate-50 rounded-lg border text-xs" />
                </div>
                <div>
                  <label class="text-[8px] text-slate-400 mb-1 block">Bas droite</label>
                  <input type="number" v-model.number="selectedZone.style.borderRadiusBottomRight" :placeholder="selectedZone.style.borderRadius || 0" min="0" class="w-full p-2 bg-slate-50 rounded-lg border text-xs" />
                </div>
                <div>
                  <label class="text-[8px] text-slate-400 mb-1 block">Bas gauche</label>
                  <input type="number" v-model.number="selectedZone.style.borderRadiusBottomLeft" :placeholder="selectedZone.style.borderRadius || 0" min="0" class="w-full p-2 bg-slate-50 rounded-lg border text-xs" />
                </div>
              </div>
            </div>
          </div>

          <!-- ESPACEMENT -->
          <div class="space-y-3 pt-4 border-t">
            <label class="text-[10px] font-black text-slate-400 uppercase">Espacement</label>

            <div>
              <label class="text-[9px] text-slate-500 mb-2 block">Padding intérieur (px)</label>
              <div class="grid grid-cols-4 gap-1">
                <div>
                  <label class="text-[8px] text-slate-400 mb-1 block text-center">T</label>
                  <input type="number" v-model.number="selectedZone.style.paddingTop" :placeholder="selectedZone.style.padding || 20" min="0" class="w-full p-1.5 bg-slate-50 rounded border text-xs text-center" />
                </div>
                <div>
                  <label class="text-[8px] text-slate-400 mb-1 block text-center">R</label>
                  <input type="number" v-model.number="selectedZone.style.paddingRight" :placeholder="selectedZone.style.padding || 20" min="0" class="w-full p-1.5 bg-slate-50 rounded border text-xs text-center" />
                </div>
                <div>
                  <label class="text-[8px] text-slate-400 mb-1 block text-center">B</label>
                  <input type="number" v-model.number="selectedZone.style.paddingBottom" :placeholder="selectedZone.style.padding || 20" min="0" class="w-full p-1.5 bg-slate-50 rounded border text-xs text-center" />
                </div>
                <div>
                  <label class="text-[8px] text-slate-400 mb-1 block text-center">L</label>
                  <input type="number" v-model.number="selectedZone.style.paddingLeft" :placeholder="selectedZone.style.padding || 20" min="0" class="w-full p-1.5 bg-slate-50 rounded border text-xs text-center" />
                </div>
              </div>
            </div>

            <div>
              <label class="text-[9px] text-slate-500 mb-2 block">Marge extérieure (px)</label>
              <div class="grid grid-cols-4 gap-1">
                <div>
                  <label class="text-[8px] text-slate-400 mb-1 block text-center">T</label>
                  <input type="number" v-model.number="selectedZone.style.marginTop" :placeholder="selectedZone.style.margin || 0" class="w-full p-1.5 bg-slate-50 rounded border text-xs text-center" />
                </div>
                <div>
                  <label class="text-[8px] text-slate-400 mb-1 block text-center">R</label>
                  <input type="number" v-model.number="selectedZone.style.marginRight" :placeholder="selectedZone.style.margin || 0" class="w-full p-1.5 bg-slate-50 rounded border text-xs text-center" />
                </div>
                <div>
                  <label class="text-[8px] text-slate-400 mb-1 block text-center">B</label>
                  <input type="number" v-model.number="selectedZone.style.marginBottom" :placeholder="selectedZone.style.margin || 0" class="w-full p-1.5 bg-slate-50 rounded border text-xs text-center" />
                </div>
                <div>
                  <label class="text-[8px] text-slate-400 mb-1 block text-center">L</label>
                  <input type="number" v-model.number="selectedZone.style.marginLeft" :placeholder="selectedZone.style.margin || 0" class="w-full p-1.5 bg-slate-50 rounded border text-xs text-center" />
                </div>
              </div>
            </div>
          </div>

          <!-- OMBRES -->
          <div class="space-y-3 pt-4 border-t">
            <label class="text-[10px] font-black text-slate-400 uppercase">Ombres</label>

            <!-- Box Shadow -->
            <div class="space-y-2">
              <div class="flex items-center gap-2">
                <input type="checkbox" v-model="selectedZone.style.boxShadow.enabled" id="enableBoxShadow" />
                <label for="enableBoxShadow" class="text-xs font-semibold">Ombre de la boîte</label>
              </div>

              <div v-if="selectedZone.style.boxShadow.enabled" class="pl-6 space-y-2">
                <div class="grid grid-cols-2 gap-2">
                  <div>
                    <label class="text-[8px] text-slate-400 mb-1 block">X (px)</label>
                    <input type="number" v-model.number="selectedZone.style.boxShadow.offsetX" class="w-full p-1.5 bg-slate-50 rounded border text-xs" />
                  </div>
                  <div>
                    <label class="text-[8px] text-slate-400 mb-1 block">Y (px)</label>
                    <input type="number" v-model.number="selectedZone.style.boxShadow.offsetY" class="w-full p-1.5 bg-slate-50 rounded border text-xs" />
                  </div>
                  <div>
                    <label class="text-[8px] text-slate-400 mb-1 block">Flou (px)</label>
                    <input type="number" v-model.number="selectedZone.style.boxShadow.blur" min="0" class="w-full p-1.5 bg-slate-50 rounded border text-xs" />
                  </div>
                  <div>
                    <label class="text-[8px] text-slate-400 mb-1 block">Étendue (px)</label>
                    <input type="number" v-model.number="selectedZone.style.boxShadow.spread" class="w-full p-1.5 bg-slate-50 rounded border text-xs" />
                  </div>
                </div>
                <div class="grid grid-cols-2 gap-2">
                  <div>
                    <label class="text-[8px] text-slate-400 mb-1 block">Couleur</label>
                    <input type="color" v-model="selectedZone.style.boxShadow.color" class="w-full h-8 rounded cursor-pointer" />
                  </div>
                  <div>
                    <label class="text-[8px] text-slate-400 mb-1 block">Opacité</label>
                    <input type="range" v-model.number="selectedZone.style.boxShadow.opacity" min="0" max="1" step="0.05" class="w-full" />
                    <div class="text-[8px] text-center text-slate-400">{{ Math.round(selectedZone.style.boxShadow.opacity * 100) }}%</div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Text Shadow -->
            <div class="space-y-2">
              <div class="flex items-center gap-2">
                <input type="checkbox" v-model="selectedZone.style.textShadow.enabled" id="enableTextShadow" />
                <label for="enableTextShadow" class="text-xs font-semibold">Ombre du texte</label>
              </div>

              <div v-if="selectedZone.style.textShadow.enabled" class="pl-6 space-y-2">
                <div class="grid grid-cols-3 gap-2">
                  <div>
                    <label class="text-[8px] text-slate-400 mb-1 block">X (px)</label>
                    <input type="number" v-model.number="selectedZone.style.textShadow.offsetX" class="w-full p-1.5 bg-slate-50 rounded border text-xs" />
                  </div>
                  <div>
                    <label class="text-[8px] text-slate-400 mb-1 block">Y (px)</label>
                    <input type="number" v-model.number="selectedZone.style.textShadow.offsetY" class="w-full p-1.5 bg-slate-50 rounded border text-xs" />
                  </div>
                  <div>
                    <label class="text-[8px] text-slate-400 mb-1 block">Flou (px)</label>
                    <input type="number" v-model.number="selectedZone.style.textShadow.blur" min="0" class="w-full p-1.5 bg-slate-50 rounded border text-xs" />
                  </div>
                </div>
                <div class="grid grid-cols-2 gap-2">
                  <div>
                    <label class="text-[8px] text-slate-400 mb-1 block">Couleur</label>
                    <input type="color" v-model="selectedZone.style.textShadow.color" class="w-full h-8 rounded cursor-pointer" />
                  </div>
                  <div>
                    <label class="text-[8px] text-slate-400 mb-1 block">Opacité</label>
                    <input type="range" v-model.number="selectedZone.style.textShadow.opacity" min="0" max="1" step="0.05" class="w-full" />
                    <div class="text-[8px] text-center text-slate-400">{{ Math.round(selectedZone.style.textShadow.opacity * 100) }}%</div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- OPACITÉ & FILTRES -->
          <div class="space-y-3 pt-4 border-t">
            <label class="text-[10px] font-black text-slate-400 uppercase">Opacité & Filtres</label>

            <div>
              <label class="text-[9px] text-slate-500 mb-1 block">Opacité globale</label>
              <input type="range" v-model.number="selectedZone.style.opacity" min="0" max="1" step="0.05" class="w-full" />
              <div class="text-[8px] text-center text-slate-400">{{ Math.round(selectedZone.style.opacity * 100) }}%</div>
            </div>

            <div>
              <label class="text-[9px] text-slate-500 mb-1 block">Flou</label>
              <input type="range" v-model.number="selectedZone.style.filters.blur" min="0" max="20" step="0.5" class="w-full" />
              <div class="text-[8px] text-center text-slate-400">{{ selectedZone.style.filters.blur }}px</div>
            </div>

            <div>
              <label class="text-[9px] text-slate-500 mb-1 block">Luminosité</label>
              <input type="range" v-model.number="selectedZone.style.filters.brightness" min="0" max="200" step="5" class="w-full" />
              <div class="text-[8px] text-center text-slate-400">{{ selectedZone.style.filters.brightness }}%</div>
            </div>

            <div>
              <label class="text-[9px] text-slate-500 mb-1 block">Contraste</label>
              <input type="range" v-model.number="selectedZone.style.filters.contrast" min="0" max="200" step="5" class="w-full" />
              <div class="text-[8px] text-center text-slate-400">{{ selectedZone.style.filters.contrast }}%</div>
            </div>

            <div>
              <label class="text-[9px] text-slate-500 mb-1 block">Saturation</label>
              <input type="range" v-model.number="selectedZone.style.filters.saturate" min="0" max="200" step="5" class="w-full" />
              <div class="text-[8px] text-center text-slate-400">{{ selectedZone.style.filters.saturate }}%</div>
            </div>

            <div>
              <label class="text-[9px] text-slate-500 mb-1 block">Teinte</label>
              <input type="range" v-model.number="selectedZone.style.filters.hueRotate" min="0" max="360" step="5" class="w-full" />
              <div class="text-[8px] text-center text-slate-400">{{ selectedZone.style.filters.hueRotate }}°</div>
            </div>
          </div>

          <!-- TRANSFORMATIONS -->
          <div class="space-y-3 pt-4 border-t">
            <label class="text-[10px] font-black text-slate-400 uppercase">Transformations</label>

            <div>
              <div class="flex justify-between items-center mb-1">
                <label class="text-[9px] text-slate-500">Rotation</label>
                <button @click="selectedZone.style.transform.rotate = 0" class="text-[8px] text-blue-500 hover:text-blue-600">Reset</button>
              </div>
              <input type="range" v-model.number="selectedZone.style.transform.rotate" min="-180" max="180" step="5" class="w-full" />
              <div class="text-[8px] text-center text-slate-400">{{ selectedZone.style.transform.rotate }}°</div>
            </div>

            <div>
              <div class="flex justify-between items-center mb-1">
                <label class="text-[9px] text-slate-500">Échelle</label>
                <button @click="selectedZone.style.transform.scale = 1" class="text-[8px] text-blue-500 hover:text-blue-600">Reset</button>
              </div>
              <input type="range" v-model.number="selectedZone.style.transform.scale" min="0.5" max="2" step="0.05" class="w-full" />
              <div class="text-[8px] text-center text-slate-400">{{ selectedZone.style.transform.scale }}x</div>
            </div>
          </div>

          <!-- Text Style -->
          <div class="space-y-3 pt-4 border-t">
            <label class="text-[10px] font-black text-slate-400 uppercase">Style du texte</label>

            <!-- Police de caractères -->
            <div>
              <label class="text-[10px] text-slate-500 mb-1 block">Police</label>
              <select v-model="selectedZone.textStyle.fontFamily" class="w-full p-2 bg-slate-50 rounded-lg border text-xs" :style="{ fontFamily: selectedZone.textStyle.fontFamily }">
                <option v-for="font in availableFonts" :key="font" :value="font" :style="{ fontFamily: font }">
                  {{ font }}
                </option>
              </select>
            </div>

            <div class="grid grid-cols-2 gap-2">
              <div>
                <label class="text-[10px] text-slate-500 mb-1 block">Taille</label>
                <input type="number" v-model.number="selectedZone.textStyle.fontSize" class="w-full p-2 bg-slate-50 rounded-lg border text-xs" />
              </div>
              <div>
                <label class="text-[10px] text-slate-500 mb-1 block">Couleur</label>
                <input type="color" v-model="selectedZone.textStyle.color" class="w-full h-9 rounded cursor-pointer" />
              </div>
            </div>
          </div>

          <!-- Delete Zone -->
          <div class="pt-4 border-t">
            <button @click="deleteZone(selectedZone.id)" class="w-full py-3 bg-red-50 text-red-500 rounded-xl text-xs font-bold hover:bg-red-100">
              Supprimer cette zone
            </button>
          </div>
        </template>

        <!-- Branding Global -->
        <template v-else>
          <!-- Logo -->
          <div class="space-y-3">
            <label class="text-[10px] font-black text-slate-400 uppercase">Logo</label>
            <div class="flex items-center gap-2">
<input type="checkbox" v-model="logoEnabled" id="showBrandLogo" />              <label for="showBrandLogo" class="text-xs">Afficher le logo</label>
            </div>

            <div v-if="config?.branding?.logo?.show" class="space-y-2">
              <MediaPicker
                v-model="config.branding.logo.url"
                placeholder="URL du logo"
                button-text="Choisir le logo"
                :agency-id="agencyId"
              />
              <select v-model="config.branding.logo.position" class="w-full p-2 bg-slate-50 rounded-lg border text-xs">
                <option value="top-left">Haut gauche</option>
                <option value="top-center">Haut centre</option>
                <option value="top-right">Haut droite</option>
                <option value="bottom-left">Bas gauche</option>
                <option value="bottom-center">Bas centre</option>
                <option value="bottom-right">Bas droite</option>
              </select>
            </div>
          </div>

          <!-- Background Global -->
          <div class="space-y-3 pt-4 border-t">
            <label class="text-[10px] font-black text-slate-400 uppercase">Fond d'écran</label>
            <select v-model="config.branding.backgroundType" class="w-full p-2 bg-slate-50 rounded-lg border text-xs">
              <option value="color">Couleur unie</option>
              <option value="gradient">Dégradé</option>
              <option value="image">Image</option>
            </select>

            <div v-if="config.branding.backgroundType === 'color'">
              <input type="color" v-model="config.branding.backgroundColor" class="w-full h-10 rounded cursor-pointer" />
            </div>

            <div v-else-if="config.branding.backgroundType === 'gradient'" class="space-y-2">
              <div class="grid grid-cols-2 gap-2">
                <div>
                  <label class="text-[9px] text-slate-500 mb-1 block">Couleur 1</label>
                  <input type="color" v-model="config.branding.gradient.color1" class="w-full h-10 rounded cursor-pointer" />
                </div>
                <div>
                  <label class="text-[9px] text-slate-500 mb-1 block">Couleur 2</label>
                  <input type="color" v-model="config.branding.gradient.color2" class="w-full h-10 rounded cursor-pointer" />
                </div>
              </div>
              <div>
                <label class="text-[9px] text-slate-500 mb-1 block">Direction</label>
                <select v-model="config.branding.gradient.direction" class="w-full p-2 bg-slate-50 rounded-lg border text-xs">
                  <option value="to right">Horizontal (gauche → droite)</option>
                  <option value="to left">Horizontal (droite → gauche)</option>
                  <option value="to bottom">Vertical (haut → bas)</option>
                  <option value="to top">Vertical (bas → haut)</option>
                  <option value="135deg">Diagonal ↘</option>
                  <option value="45deg">Diagonal ↗</option>
                </select>
              </div>
            </div>

            <div v-else-if="config.branding.backgroundType === 'image'" class="space-y-2">
              <label class="text-[9px] text-slate-500 mb-1 block">URL Image de fond</label>
              <MediaPicker
                v-model="config.branding.backgroundImage"
                placeholder="URL de l'image de fond globale"
                button-text="Choisir l'image de fond"
                :agency-id="agencyId"
              />
            </div>
          </div>

          <!-- Colors -->
          <div class="space-y-3 pt-4 border-t">
            <label class="text-[10px] font-black text-slate-400 uppercase">Palette de couleurs</label>
            <div class="grid grid-cols-2 gap-2">
              <div>
                <label class="text-[9px] text-slate-500">Primaire</label>
                <input type="color" v-model="config.branding.colors.primary" class="w-full h-8 rounded cursor-pointer" />
              </div>
              <div>
                <label class="text-[9px] text-slate-500">Secondaire</label>
                <input type="color" v-model="config.branding.colors.secondary" class="w-full h-8 rounded cursor-pointer" />
              </div>
            </div>
          </div>

          <!-- Police globale -->
          <div class="space-y-3 pt-4 border-t">
            <label class="text-[10px] font-black text-slate-400 uppercase">Police par défaut</label>
            <p class="text-[9px] text-slate-500 mb-2">Cette police sera utilisée par défaut sur tout l'écran</p>
            <select v-model="config.branding.fonts.primary" class="w-full p-2 bg-slate-50 rounded-lg border text-xs" :style="{ fontFamily: config.branding.fonts.primary }">
              <option v-for="font in availableFonts" :key="font" :value="font" :style="{ fontFamily: font }">
                {{ font }}
              </option>
            </select>
          </div>

          <!-- Audio & Notifications -->
          <div class="space-y-3 pt-4 border-t">
            <label class="text-[10px] font-black text-slate-400 uppercase">🔊 Audio & Notifications</label>

            <div class="flex items-center gap-2">
              <input type="checkbox" v-model="config.audio.enabled" id="audioEnabled" />
              <label for="audioEnabled" class="text-xs font-semibold">Activer le son</label>
            </div>

            <div v-if="config.audio.enabled" class="space-y-3 pl-4">
              <div>
                <label class="text-[9px] text-slate-500 mb-1 block">Volume</label>
                <input type="range" v-model.number="config.audio.volume" min="0" max="1" step="0.1" class="w-full" />
                <div class="text-[8px] text-center text-slate-400">{{ Math.round(config.audio.volume * 100) }}%</div>
              </div>

              <div>
                <label class="text-[9px] text-slate-500 mb-1 block">Son d'appel</label>
                <input type="text" v-model="config.audio.callSound" placeholder="URL du son..." class="w-full p-2 bg-slate-50 rounded-lg border text-xs font-mono" />
              </div>

              <div>
                <label class="text-[9px] text-slate-500 mb-1 block">Son de rappel</label>
                <input type="text" v-model="config.audio.recallSound" placeholder="URL du son..." class="w-full p-2 bg-slate-50 rounded-lg border text-xs font-mono" />
              </div>

              <div class="flex items-center gap-2">
                <input type="checkbox" v-model="config.audio.useVoice" id="useVoice" />
                <label for="useVoice" class="text-xs">Synthèse vocale</label>
              </div>

              <div v-if="config.audio.useVoice">
                <label class="text-[9px] text-slate-500 mb-1 block">Langue</label>
                <select v-model="config.audio.voiceLang" class="w-full p-2 bg-slate-50 rounded-lg border text-xs">
                  <option value="fr-FR">Français</option>
                  <option value="en-US">Anglais (US)</option>
                  <option value="en-GB">Anglais (UK)</option>
                  <option value="es-ES">Espagnol</option>
                  <option value="de-DE">Allemand</option>
                  <option value="it-IT">Italien</option>
                  <option value="ar-SA">Arabe</option>
                </select>
              </div>
            </div>
          </div>

          <!-- Animations -->
          <div class="space-y-3 pt-4 border-t">
            <label class="text-[10px] font-black text-slate-400 uppercase">🎬 Animations</label>

            <div class="flex items-center gap-2">
              <input type="checkbox" v-model="config.animations.enabled" id="animEnabled" />
              <label for="animEnabled" class="text-xs font-semibold">Activer les animations</label>
            </div>

            <div v-if="config.animations.enabled" class="space-y-3 pl-4">
              <div>
                <label class="text-[9px] text-slate-500 mb-1 block">Transition des tickets</label>
                <select v-model="config.animations.ticketTransition" class="w-full p-2 bg-slate-50 rounded-lg border text-xs">
                  <option value="fade">Fondu</option>
                  <option value="slide">Glissement</option>
                  <option value="zoom">Zoom</option>
                  <option value="bounce">Rebond</option>
                  <option value="none">Aucune</option>
                </select>
              </div>

              <div>
                <label class="text-[9px] text-slate-500 mb-1 block">Durée (ms)</label>
                <input type="range" v-model.number="config.animations.transitionDuration" min="100" max="2000" step="100" class="w-full" />
                <div class="text-[8px] text-center text-slate-400">{{ config.animations.transitionDuration }}ms</div>
              </div>
            </div>
          </div>

          <!-- Banner d'Appel -->
          <div class="space-y-3 pt-4 border-t">
            <label class="text-[10px] font-black text-slate-400 uppercase">📢 Banner d'Appel</label>
            <p class="text-[9px] text-slate-500">Personnalisation de la banner qui s'affiche lors de l'appel d'un ticket</p>

            <!-- Preview de la banner -->
            <div class="space-y-2 p-3 bg-slate-900 rounded-xl">
              <div class="text-[9px] font-bold text-slate-400 mb-2">APERÇU EN DIRECT</div>

              <!-- Toggle pour changer entre normal et rappel -->
              <div class="flex gap-2 mb-3">
                <button
                  @click="bannerPreviewMode = 'normal'"
                  :class="['flex-1 py-1.5 px-2 rounded-lg text-[9px] font-bold transition-all',
                          bannerPreviewMode === 'normal' ? 'bg-blue-500 text-white' : 'bg-slate-700 text-slate-300']">
                  Appel Normal
                </button>
                <button
                  @click="bannerPreviewMode = 'recall'"
                  :class="['flex-1 py-1.5 px-2 rounded-lg text-[9px] font-bold transition-all',
                          bannerPreviewMode === 'recall' ? 'bg-amber-500 text-white' : 'bg-slate-700 text-slate-300']">
                  Rappel
                </button>
              </div>

              <!-- Mini preview de la banner -->
              <div class="relative p-2 rounded-lg overflow-hidden"
                   :style="{
                     backgroundColor: bannerPreviewMode === 'recall'
                       ? config.banner?.recall?.backgroundColor || 'rgb(15, 23, 42)'
                       : config.banner?.normal?.backgroundColor || 'rgba(255, 255, 255, 0.95)',
                     borderWidth: (config.banner?.borderWidth || 1) + 'px',
                     borderStyle: 'solid',
                     borderColor: bannerPreviewMode === 'recall'
                       ? config.banner?.recall?.borderColor || 'rgba(245, 158, 11, 0.4)'
                       : config.banner?.normal?.borderColor || 'rgba(255, 255, 255, 0.2)',
                     borderRadius: (config.banner?.borderRadius || 32) / 2 + 'px'
                   }">
                <div class="flex items-center justify-between gap-2">
                  <!-- Icône + Numéro -->
                  <div class="flex items-center gap-2">
                    <div class="w-6 h-6 rounded-lg flex items-center justify-center text-xs"
                         :style="{
                           backgroundColor: bannerPreviewMode === 'recall'
                             ? config.banner?.recall?.iconBackgroundColor || 'rgba(245, 158, 11, 0.1)'
                             : config.banner?.normal?.iconBackgroundColor || 'rgb(239, 246, 255)',
                           color: bannerPreviewMode === 'recall'
                             ? config.banner?.recall?.iconColor || '#f59e0b'
                             : config.banner?.normal?.iconColor || '#1e3a8a'
                         }">
                      💊
                    </div>
                    <div>
                      <div class="opacity-50 uppercase"
                           :style="{
                             color: bannerPreviewMode === 'recall'
                               ? config.banner?.recall?.textColor || 'rgb(251, 191, 36)'
                               : config.banner?.normal?.textColor || 'rgb(15, 23, 42)',
                             fontSize: (config.banner?.queueNameSize || 24) / 4 + 'px',
                             fontFamily: config.banner?.fontFamily || 'Plus Jakarta Sans'
                           }">
                        Service
                      </div>
                      <div class="font-black leading-none"
                           :style="{
                             color: bannerPreviewMode === 'recall'
                               ? config.banner?.recall?.textColor || 'rgb(251, 191, 36)'
                               : config.banner?.normal?.textColor || 'rgb(15, 23, 42)',
                             fontSize: (config.banner?.ticketNumberSize || 144) / 10 + 'px',
                             fontFamily: config.banner?.fontFamily || 'Plus Jakarta Sans'
                           }">
                        A123
                      </div>
                    </div>
                  </div>

                  <!-- Badge Caisse -->
                  <div class="px-2 py-1 rounded font-bold"
                       :style="{
                         borderWidth: '1px',
                         borderStyle: 'solid',
                         borderColor: bannerPreviewMode === 'recall'
                           ? config.banner?.recall?.badgeBorderColor || 'rgb(245, 158, 11)'
                           : config.banner?.normal?.badgeBorderColor || 'rgb(29, 78, 216)',
                         color: bannerPreviewMode === 'recall'
                           ? config.banner?.recall?.badgeColor || 'rgb(245, 158, 11)'
                           : config.banner?.normal?.badgeColor || 'rgb(29, 78, 216)',
                         fontSize: (config.banner?.badgeSize || 56) / 6 + 'px',
                         fontFamily: config.banner?.fontFamily || 'Plus Jakarta Sans'
                       }">
                    C1
                  </div>
                </div>

                <!-- Badge Rappel -->
                <div v-if="bannerPreviewMode === 'recall'"
                     class="absolute top-0 right-2 px-2 py-0.5 rounded-b text-[7px] font-bold"
                     :style="{
                       backgroundColor: config.banner?.recall?.recallBadgeColor || 'rgb(245, 158, 11)',
                       color: 'rgb(15, 23, 42)'
                     }">
                  RAPPEL
                </div>
              </div>
            </div>

            <!-- Sélecteur de style de banner -->
            <div class="space-y-2">
              <label class="text-[9px] text-slate-500 mb-1 block font-bold">Style de la banner</label>
              <select v-model="bannerStylePreset" @change="applyBannerPreset" class="w-full p-2 bg-slate-50 rounded-lg border text-xs font-semibold">
                <option value="default">🔵 Bleu & Blanc (Défaut)</option>
                <option value="green">🟢 Vert Pharmacie</option>
                <option value="dark">⚫ Mode Sombre</option>
                <option value="orange">🟠 Orange Moderne</option>
                <option value="purple">🟣 Violet Élégant</option>
                <option value="custom">🎨 Personnalisé</option>
              </select>
            </div>

            <!-- Section de personnalisation détaillée (visible seulement en mode custom) -->
            <div v-if="bannerStylePreset === 'custom'" class="space-y-3 animate-in fade-in slide-in-from-top-2">

            <!-- Appel Normal -->
            <div class="space-y-3 p-3 bg-blue-50 rounded-lg border border-blue-100">
              <div class="text-xs font-bold text-blue-700">Appel Normal</div>

              <div>
                <label class="text-[9px] text-slate-500 mb-1 block">Couleur de fond</label>
                <div class="flex gap-2">
                  <input type="color" v-model="config.banner.normal.backgroundColor" class="w-20 h-8 rounded cursor-pointer" />
                  <input type="text" v-model="config.banner.normal.backgroundColor" class="flex-1 px-2 py-1 text-xs border rounded" placeholder="rgba(...) ou #HEX" />
                </div>
              </div>

              <div>
                <label class="text-[9px] text-slate-500 mb-1 block">Couleur de bordure</label>
                <div class="flex gap-2">
                  <input type="color" v-model="config.banner.normal.borderColor" class="w-20 h-8 rounded cursor-pointer" />
                  <input type="text" v-model="config.banner.normal.borderColor" class="flex-1 px-2 py-1 text-xs border rounded" placeholder="rgba(...) ou #HEX" />
                </div>
              </div>

              <div>
                <label class="text-[9px] text-slate-500 mb-1 block">Couleur du texte</label>
                <div class="flex gap-2">
                  <input type="color" v-model="config.banner.normal.textColor" class="w-20 h-8 rounded cursor-pointer" />
                  <input type="text" v-model="config.banner.normal.textColor" class="flex-1 px-2 py-1 text-xs border rounded" placeholder="rgba(...) ou #HEX" />
                </div>
              </div>

              <div>
                <label class="text-[9px] text-slate-500 mb-1 block">Couleur de l'icône</label>
                <input type="color" v-model="config.banner.normal.iconColor" class="w-full h-8 rounded cursor-pointer" />
              </div>

              <div>
                <label class="text-[9px] text-slate-500 mb-1 block">Couleur fond icône</label>
                <div class="flex gap-2">
                  <input type="color" v-model="config.banner.normal.iconBackgroundColor" class="w-20 h-8 rounded cursor-pointer" />
                  <input type="text" v-model="config.banner.normal.iconBackgroundColor" class="flex-1 px-2 py-1 text-xs border rounded" placeholder="rgba(...) ou #HEX" />
                </div>
              </div>

              <div>
                <label class="text-[9px] text-slate-500 mb-1 block">Couleur badge caisse</label>
                <input type="color" v-model="config.banner.normal.badgeColor" class="w-full h-8 rounded cursor-pointer" />
              </div>

              <div>
                <label class="text-[9px] text-slate-500 mb-1 block">Couleur bordure badge</label>
                <input type="color" v-model="config.banner.normal.badgeBorderColor" class="w-full h-8 rounded cursor-pointer" />
              </div>
            </div>

            <!-- Rappel -->
            <div class="space-y-3 p-3 bg-amber-50 rounded-lg border border-amber-100">
              <div class="text-xs font-bold text-amber-700">Rappel</div>

              <div>
                <label class="text-[9px] text-slate-500 mb-1 block">Couleur de fond</label>
                <div class="flex gap-2">
                  <input type="color" v-model="config.banner.recall.backgroundColor" class="w-20 h-8 rounded cursor-pointer" />
                  <input type="text" v-model="config.banner.recall.backgroundColor" class="flex-1 px-2 py-1 text-xs border rounded" placeholder="rgba(...) ou #HEX" />
                </div>
              </div>

              <div>
                <label class="text-[9px] text-slate-500 mb-1 block">Couleur de bordure</label>
                <div class="flex gap-2">
                  <input type="color" v-model="config.banner.recall.borderColor" class="w-20 h-8 rounded cursor-pointer" />
                  <input type="text" v-model="config.banner.recall.borderColor" class="flex-1 px-2 py-1 text-xs border rounded" placeholder="rgba(...) ou #HEX" />
                </div>
              </div>

              <div>
                <label class="text-[9px] text-slate-500 mb-1 block">Couleur du texte</label>
                <div class="flex gap-2">
                  <input type="color" v-model="config.banner.recall.textColor" class="w-20 h-8 rounded cursor-pointer" />
                  <input type="text" v-model="config.banner.recall.textColor" class="flex-1 px-2 py-1 text-xs border rounded" placeholder="rgba(...) ou #HEX" />
                </div>
              </div>

              <div>
                <label class="text-[9px] text-slate-500 mb-1 block">Couleur de l'icône</label>
                <input type="color" v-model="config.banner.recall.iconColor" class="w-full h-8 rounded cursor-pointer" />
              </div>

              <div>
                <label class="text-[9px] text-slate-500 mb-1 block">Couleur fond icône</label>
                <div class="flex gap-2">
                  <input type="color" v-model="config.banner.recall.iconBackgroundColor" class="w-20 h-8 rounded cursor-pointer" />
                  <input type="text" v-model="config.banner.recall.iconBackgroundColor" class="flex-1 px-2 py-1 text-xs border rounded" placeholder="rgba(...) ou #HEX" />
                </div>
              </div>

              <div>
                <label class="text-[9px] text-slate-500 mb-1 block">Couleur badge caisse</label>
                <input type="color" v-model="config.banner.recall.badgeColor" class="w-full h-8 rounded cursor-pointer" />
              </div>

              <div>
                <label class="text-[9px] text-slate-500 mb-1 block">Couleur bordure badge</label>
                <input type="color" v-model="config.banner.recall.badgeBorderColor" class="w-full h-8 rounded cursor-pointer" />
              </div>

              <div>
                <label class="text-[9px] text-slate-500 mb-1 block">Couleur badge "Rappel"</label>
                <input type="color" v-model="config.banner.recall.recallBadgeColor" class="w-full h-8 rounded cursor-pointer" />
              </div>
            </div>

            <!-- Style commun -->
            <div class="space-y-2 p-3 bg-slate-50 rounded-lg">
              <div class="text-xs font-bold text-slate-700">Style Général</div>

              <div>
                <label class="text-[9px] text-slate-500 mb-1 block">Hauteur de la banner (px)</label>
                <input type="range" v-model.number="config.banner.height" min="120" max="300" step="10" class="w-full" />
                <div class="text-[8px] text-center text-slate-400">{{ config.banner.height }}px</div>
              </div>

              <div>
                <label class="text-[9px] text-slate-500 mb-1 block">Arrondi bordure (px)</label>
                <input type="range" v-model.number="config.banner.borderRadius" min="0" max="64" step="4" class="w-full" />
                <div class="text-[8px] text-center text-slate-400">{{ config.banner.borderRadius }}px</div>
              </div>

              <div>
                <label class="text-[9px] text-slate-500 mb-1 block">Largeur bordure (px)</label>
                <input type="range" v-model.number="config.banner.borderWidth" min="0" max="8" step="1" class="w-full" />
                <div class="text-[8px] text-center text-slate-400">{{ config.banner.borderWidth }}px</div>
              </div>

              <div>
                <label class="text-[9px] text-slate-500 mb-1 block">Opacité ombre</label>
                <input type="range" v-model.number="config.banner.shadowOpacity" min="0" max="1" step="0.1" class="w-full" />
                <div class="text-[8px] text-center text-slate-400">{{ Math.round(config.banner.shadowOpacity * 100) }}%</div>
              </div>

              <div>
                <label class="text-[9px] text-slate-500 mb-1 block">Durée d'affichage (secondes)</label>
                <div class="grid grid-cols-2 gap-2">
                  <div>
                    <label class="text-[8px] text-slate-400 mb-1 block">Normal</label>
                    <input type="number" v-model.number="config.banner.displayDuration.normal" min="1" max="30" class="w-full p-2 bg-white rounded border text-xs text-center" />
                  </div>
                  <div>
                    <label class="text-[8px] text-slate-400 mb-1 block">Rappel</label>
                    <input type="number" v-model.number="config.banner.displayDuration.recall" min="1" max="30" class="w-full p-2 bg-white rounded border text-xs text-center" />
                  </div>
                </div>
              </div>
            </div>

            <!-- Typographie -->
            <div class="space-y-2 p-3 bg-indigo-50 rounded-lg border border-indigo-100">
              <div class="text-xs font-bold text-indigo-700">Typographie</div>

              <div>
                <label class="text-[9px] text-slate-500 mb-1 block">Police du texte</label>
                <select v-model="config.banner.fontFamily" class="w-full p-2 bg-white rounded border text-xs" :style="{ fontFamily: config.banner.fontFamily }">
                  <option v-for="font in availableFonts" :key="font" :value="font" :style="{ fontFamily: font }">
                    {{ font }}
                  </option>
                </select>
              </div>

              <div>
                <label class="text-[9px] text-slate-500 mb-1 block">Taille numéro ticket</label>
                <input type="range" v-model.number="config.banner.ticketNumberSize" min="60" max="160" step="10" class="w-full" />
                <div class="text-[8px] text-center text-slate-400">{{ config.banner.ticketNumberSize }}px (base)</div>
              </div>

              <div>
                <label class="text-[9px] text-slate-500 mb-1 block">Taille nom de la queue</label>
                <input type="range" v-model.number="config.banner.queueNameSize" min="12" max="32" step="2" class="w-full" />
                <div class="text-[8px] text-center text-slate-400">{{ config.banner.queueNameSize }}px (base)</div>
              </div>

              <div>
                <label class="text-[9px] text-slate-500 mb-1 block">Taille badge caisse</label>
                <input type="range" v-model.number="config.banner.badgeSize" min="24" max="72" step="4" class="w-full" />
                <div class="text-[8px] text-center text-slate-400">{{ config.banner.badgeSize }}px (base)</div>
              </div>

              <div>
                <label class="text-[9px] text-slate-500 mb-1 block">Taille instruction</label>
                <input type="range" v-model.number="config.banner.instructionSize" min="10" max="24" step="2" class="w-full" />
                <div class="text-[8px] text-center text-slate-400">{{ config.banner.instructionSize }}px (base)</div>
              </div>

              <p class="text-[8px] text-slate-500 italic mt-2">Les tailles s'adaptent automatiquement en responsive</p>
            </div>

            </div><!-- Fin du div v-if="bannerStylePreset === 'custom'" -->
          </div>
        </template>
      </div>
    </aside>
  </div>
</template>

<script setup>
import { ref, onMounted, computed, nextTick, watch } from 'vue';
import { toast } from "vue3-toastify";
import { useRoute } from 'vue-router';
import MediaPicker from '@/components/MediaPicker.vue';
import PharmacyIcon from '@/components/PharmacyIcons.vue';

const route = useRoute();
const agencyId = route.params.agencyId;

const selectedZone = ref(null);
const queues = ref([]);
const zoomLevel = ref(100);
const historyStack = ref([]);
const historyIndex = ref(-1);
const copiedStyle = ref(null);

// ✅ NOUVEAU : Gestion multi-écrans
const selectedDeviceId = ref(null); // null = config par défaut
const availableScreens = ref([]);

// Sélecteur de style de banner
const bannerStylePreset = ref('default');
const bannerPreviewMode = ref('normal'); // 'normal' ou 'recall'

const currentTime = ref(new Date().toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' }));
const currentDate = ref(new Date().toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' }));

// Update clock
setInterval(() => {
  currentTime.value = new Date().toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
  currentDate.value = new Date().toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' });
}, 1000);

// === TEMPLATES PRÉDÉFINIS - THÈME PHARMACIE ===
const templates = [
  {
    name: 'Pharmacie Moderne',
    icon: '💊',
    config: {
      layout: { type: 'split_horizontal', gap: 20 },
      branding: {
        backgroundColor: '#F0FDF4',
        backgroundType: 'gradient',
        gradient: { color1: '#F0FDF4', color2: '#DCFCE7', direction: 'to bottom' },
        colors: { primary: '#16A34A', secondary: '#22C55E', text: '#166534', textSecondary: '#4ADE80' }
      },
      zones: [
        {
          id: 'zone-pharma-modern-1',
          name: 'Ticket Appelé',
          position: { x: 0, y: 0, width: 50, height: 100 },
          content: {
            type: 'queue_display',
            ticketFilter: 'CALLED',
            maxTickets: 1,
            gridColumns: 1,
            calledTicketDisplayFormat: 'block',
            maxCalledTickets: 5,
            ticketDisplayInfo: {
              showNumber: true,
              showQueueName: true,
              showCounterName: true,
              showWaitTime: false
            },
            calledTicketStyle: {
              fontFamily: 'Poppins',
              fontSize: 72,
              color: '#166534'
            },
            waitingTicketStyle: {
              fontFamily: 'Poppins',
              fontSize: 32,
              color: '#166534',
              backgroundColor: 'rgba(22, 163, 74, 0.15)',
              padding: 24,
              borderRadius: 16
            }
          },
          style: {
            backgroundColor: '#FFFFFF',
            backgroundType: 'color',
            padding: 32,
            borderRadius: 20,
            boxShadow: { enabled: true, offsetX: 0, offsetY: 6, blur: 16, spread: 0, color: '#16A34A', opacity: 0.12 }
          },
          textStyle: { fontFamily: 'Poppins', fontSize: 22, color: '#166534' }
        },
        {
          id: 'zone-pharma-modern-2',
          name: 'File d\'attente',
          position: { x: 50, y: 0, width: 50, height: 100 },
          content: {
            type: 'queue_display',
            ticketFilter: 'WAITING',
            maxTickets: 15,
            gridColumns: 3,
            ticketDisplayInfo: {
              showNumber: true,
              showQueueName: true,
              showCounterName: false,
              showWaitTime: true
            },
            waitingTicketStyle: {
              fontFamily: 'Poppins',
              fontSize: 26,
              color: '#166534',
              backgroundColor: 'rgba(220, 252, 231, 0.6)',
              padding: 18,
              borderRadius: 14
            }
          },
          style: {
            backgroundColor: '#F9FFF9',
            backgroundType: 'color',
            padding: 28,
            borderRadius: 20
          },
          textStyle: { fontFamily: 'Poppins', fontSize: 18, color: '#15803D' }
        }
      ]
    }
  },
  {
    name: 'Pharmacie Premium',
    icon: '✨',
    config: {
      layout: { type: 'single', gap: 24 },
      branding: {
        backgroundColor: '#FFFFFF',
        backgroundType: 'gradient',
        gradient: { color1: '#FFFFFF', color2: '#FEF3C7', direction: '135deg' },
        colors: { primary: '#D97706', secondary: '#F59E0B', text: '#92400E', textSecondary: '#B45309' }
      },
      zones: [
        {
          id: 'zone-pharma-premium-1',
          name: 'Zone Principale',
          position: { x: 0, y: 0, width: 100, height: 100 },
          content: {
            type: 'queue_display',
            ticketFilter: 'BOTH',
            maxTickets: 18,
            gridColumns: 4,
            calledTicketDisplayFormat: 'inline',
            maxCalledTickets: 5,
            ticketDisplayInfo: {
              showNumber: true,
              showQueueName: true,
              showCounterName: true,
              showWaitTime: false
            },
            calledTicketStyle: {
              fontFamily: 'Montserrat',
              fontSize: 48,
              color: '#92400E'
            },
            waitingTicketStyle: {
              fontFamily: 'Montserrat',
              fontSize: 30,
              color: '#92400E',
              backgroundColor: 'rgba(217, 119, 6, 0.12)',
              padding: 22,
              borderRadius: 16
            }
          },
          style: {
            backgroundColor: 'rgba(255, 255, 255, 0.9)',
            backgroundType: 'color',
            padding: 36,
            borderRadius: 24,
            borderWidth: 3,
            borderColor: '#F59E0B',
            borderStyle: 'solid',
            boxShadow: { enabled: true, offsetX: 0, offsetY: 10, blur: 30, spread: 0, color: '#D97706', opacity: 0.2 }
          },
          textStyle: { fontFamily: 'Montserrat', fontSize: 20, color: '#92400E' }
        }
      ]
    }
  },
  {
    name: 'Pharmacie Classique',
    icon: '🏥',
    config: {
      layout: { type: 'split_vertical', gap: 16 },
      branding: {
        backgroundColor: '#ECFDF5',
        backgroundType: 'color',
        colors: { primary: '#059669', secondary: '#10B981', text: '#065F46', textSecondary: '#047857' }
      },
      zones: [
        {
          id: 'zone-pharma-classic-1',
          name: 'Ticket en cours',
          position: { x: 0, y: 0, width: 100, height: 35 },
          content: {
            type: 'queue_display',
            ticketFilter: 'CALLED',
            maxTickets: 2,
            gridColumns: 2,
            calledTicketDisplayFormat: 'compact',
            maxCalledTickets: 5,
            ticketDisplayInfo: {
              showNumber: true,
              showQueueName: true,
              showCounterName: true,
              showWaitTime: false
            },
            calledTicketStyle: {
              fontFamily: 'Raleway',
              fontSize: 52,
              color: '#065F46'
            },
            waitingTicketStyle: {
              fontFamily: 'Raleway',
              fontSize: 38,
              color: '#065F46',
              backgroundColor: 'rgba(5, 150, 105, 0.15)',
              padding: 26,
              borderRadius: 12
            }
          },
          style: {
            backgroundColor: '#FFFFFF',
            backgroundType: 'color',
            padding: 28,
            borderRadius: 12,
            borderWidth: 3,
            borderColor: '#059669',
            borderStyle: 'solid'
          },
          textStyle: { fontFamily: 'Raleway', fontSize: 20, color: '#065F46' }
        },
        {
          id: 'zone-pharma-classic-2',
          name: 'Prochains tickets',
          position: { x: 0, y: 35, width: 100, height: 65 },
          content: {
            type: 'queue_display',
            ticketFilter: 'WAITING',
            maxTickets: 24,
            gridColumns: 6,
            ticketDisplayInfo: {
              showNumber: true,
              showQueueName: false,
              showCounterName: false,
              showWaitTime: false
            },
            waitingTicketStyle: {
              fontFamily: 'Raleway',
              fontSize: 22,
              color: '#065F46',
              backgroundColor: 'rgba(236, 253, 245, 0.8)',
              padding: 14,
              borderRadius: 10
            }
          },
          style: {
            backgroundColor: '#F0FDF4',
            backgroundType: 'color',
            padding: 24,
            borderRadius: 12
          },
          textStyle: { fontFamily: 'Raleway', fontSize: 16, color: '#047857' }
        }
      ]
    }
  },
  {
    name: 'Pharmacie Simple',
    icon: '⚕️',
    config: {
      layout: { type: 'single', gap: 20 },
      branding: {
        backgroundColor: '#FFFFFF',
        backgroundType: 'color',
        colors: { primary: '#14B8A6', secondary: '#2DD4BF', text: '#0F766E', textSecondary: '#14B8A6' }
      },
      zones: [
        {
          id: 'zone-pharma-simple-1',
          name: 'Zone Unique',
          position: { x: 0, y: 0, width: 100, height: 100 },
          content: {
            type: 'queue_display',
            ticketFilter: 'BOTH',
            maxTickets: 12,
            gridColumns: 3,
            calledTicketDisplayFormat: 'block',
            maxCalledTickets: 5,
            ticketDisplayInfo: {
              showNumber: true,
              showQueueName: true,
              showCounterName: true,
              showWaitTime: false
            },
            calledTicketStyle: {
              fontFamily: 'Inter',
              fontSize: 56,
              color: '#0F766E'
            },
            waitingTicketStyle: {
              fontFamily: 'Inter',
              fontSize: 44,
              color: '#0F766E',
              backgroundColor: 'rgba(20, 184, 166, 0.08)',
              padding: 28,
              borderRadius: 8
            }
          },
          style: {
            backgroundColor: '#F0FDFA',
            backgroundType: 'color',
            padding: 40,
            borderRadius: 16,
            borderWidth: 1,
            borderColor: '#99F6E4',
            borderStyle: 'solid'
          },
          textStyle: { fontFamily: 'Inter', fontSize: 24, color: '#0F766E' }
        }
      ]
    }
  }
];

const config = ref({
  agencyId: agencyId,
  enabled: true,
  display: {
    orientation: 'horizontal'
  },
  layout: {
    type: 'single',
    gridColumns: 1,
    gridRows: 1,
    gap: 20,
    padding: 24 // Padding global de la page (en px)
  },
  zones: [
    {
      id: 'zone-1',
      name: 'Zone Principale',
      position: { x: 0, y: 0, width: 100, height: 100 },
      content: {
        type: 'queue_display',
        queueIds: [],
        displayMode: 'list',
        ticketFilter: 'BOTH',
        maxTickets: 10,
        gridColumns: 2,
        calledTicketStyle: {
          fontFamily: 'Plus Jakarta Sans',
          fontSize: 48,
          color: '#1E293B'
        },
        waitingTicketStyle: {
          fontFamily: 'Plus Jakarta Sans',
          fontSize: 24,
          color: '#1E293B',
          backgroundColor: 'rgba(255, 255, 255, 0.1)',
          padding: 16,
          borderRadius: 12
        }
      },
      style: {
        backgroundColor: '#FFFFFF',
        backgroundType: 'color',
        gradient: {
          color1: '#3B82F6',
          color2: '#10B981',
          direction: 'to right'
        },
        backgroundImage: '',
        padding: 20,
        borderRadius: 12
      },
      textStyle: {
        fontFamily: 'Plus Jakarta Sans',
        fontSize: 16,
        color: '#1E293B',
        textAlign: 'left'
      }
    }
  ],
  branding: {
    logo: {
      show: false,
      url: '',
      position: 'top-left',
      size: 80
    },
    backgroundColor: '#F8FAFC',
    backgroundType: 'color',
    gradient: {
      color1: '#3B82F6',
      color2: '#10B981',
      direction: 'to right'
    },
    backgroundImage: '',
    colors: {
      primary: '#3B82F6',
      secondary: '#10B981',
      text: '#1E293B',
      textSecondary: '#64748B'
    },
    fonts: {
      primary: 'Plus Jakarta Sans'
    }
  },
  refreshInterval: 5000,
  targetResolution: {
    width: 1920,
    height: 1080
  },
  audio: {
    enabled: true,
    volume: 0.8,
    callSound: 'https://assets.mixkit.co/active_storage/sfx/2869/2869-preview.mp3',
    recallSound: 'https://assets.mixkit.co/active_storage/sfx/2869/2869-preview.mp3',
    useVoice: true,
    voiceLang: 'fr-FR'
  },
  animations: {
    enabled: true,
    ticketTransition: 'fade',
    transitionDuration: 500
  },
  banner: {
    normal: {
      backgroundColor: 'rgba(255, 255, 255, 0.95)',
      borderColor: 'rgba(255, 255, 255, 0.2)',
      textColor: 'rgb(15, 23, 42)',
      iconColor: '#1e3a8a',
      iconBackgroundColor: 'rgb(239, 246, 255)',
      badgeColor: 'rgb(29, 78, 216)',
      badgeBorderColor: 'rgb(29, 78, 216)'
    },
    recall: {
      backgroundColor: 'rgb(15, 23, 42)',
      borderColor: 'rgba(245, 158, 11, 0.4)',
      textColor: 'rgb(251, 191, 36)',
      iconColor: '#f59e0b',
      iconBackgroundColor: 'rgba(245, 158, 11, 0.1)',
      badgeColor: 'rgb(245, 158, 11)',
      badgeBorderColor: 'rgb(245, 158, 11)',
      recallBadgeColor: 'rgb(245, 158, 11)'
    },
    borderRadius: 32,
    borderWidth: 1,
    shadowOpacity: 0.3,
    displayDuration: {
      normal: 5,
      recall: 7
    },
    fontFamily: 'Plus Jakarta Sans',
    ticketNumberSize: 144,
    queueNameSize: 24,
    badgeSize: 56,
    instructionSize: 20,
    height: 180
  }
});

// === MIGRATION AUTOMATIQUE DES ZONES (BACKWARD COMPATIBILITY) ===
const migrateZoneStyle = (zone) => {
  if (!zone) return zone;
  if (!zone.style) zone.style = {};
  if (!zone.content) zone.content = { type: 'text', text: '' };
  if (!zone.textStyle) zone.textStyle = { fontFamily: 'Plus Jakarta Sans', fontSize: 16, color: '#1E293B', textAlign: 'left' };
  if (!zone.position) zone.position = { x: 0, y: 0, width: 100, height: 100 };

  const style = zone.style;

  // Initialiser boxShadow si absent
  if (!style.boxShadow) {
    style.boxShadow = {
      enabled: false,
      offsetX: 0, offsetY: 4, blur: 6, spread: 0,
      color: '#000000', opacity: 0.1
    };
  }

  // Initialiser textShadow si absent
  if (!style.textShadow) {
    style.textShadow = {
      enabled: false,
      offsetX: 0, offsetY: 2, blur: 4,
      color: '#000000', opacity: 0.5
    };
  }

  // Initialiser filters si absent
  if (!style.filters) {
    style.filters = {
      blur: 0, brightness: 100, contrast: 100,
      saturate: 100, hueRotate: 0
    };
  }

  // Initialiser transform si absent
  if (!style.transform) {
    style.transform = { rotate: 0, scale: 1 };
  }

  // Initialiser border si absent
  if (style.borderWidth === undefined) style.borderWidth = 0;
  if (!style.borderColor) style.borderColor = '#000000';
  if (!style.borderStyle) style.borderStyle = 'solid';

  // Initialiser opacity si absent
  if (style.opacity === undefined) style.opacity = 1;

  // Initialiser waitingTicketStyle pour les zones queue_display
  if (zone.content && zone.content.type === 'queue_display') {
    if (!zone.content.queueIds) zone.content.queueIds = [];
    if (!zone.content.displayMode) zone.content.displayMode = 'list';
    if (!zone.content.ticketFilter) zone.content.ticketFilter = 'BOTH';
    if (!zone.content.calledTicketDisplayFormat) zone.content.calledTicketDisplayFormat = 'block';

    if (!zone.content.ticketDisplayInfo) {
      zone.content.ticketDisplayInfo = {
        showNumber: true,
        showQueueName: true,
        showCounterName: true,
        showWaitTime: false
      };
    }

    if (!zone.content.counterStyle) {
      zone.content.counterStyle = {
        fontFamily: zone.textStyle?.fontFamily || 'Plus Jakarta Sans',
        fontSize: Math.round((zone.textStyle?.fontSize || 32) * 1.2),
        fontWeight: '700',
        color: zone.textStyle?.color || '#FFFFFF',
        backgroundColor: 'transparent',
        padding: 12,
        borderRadius: 12
      };
    }
    if (!zone.content.waitingTicketStyle) {
      zone.content.waitingTicketStyle = {
        fontFamily: zone.textStyle?.fontFamily || 'Plus Jakarta Sans',
        fontSize: 24,
        color: zone.textStyle?.color || '#1E293B',
        backgroundColor: 'rgba(255, 255, 255, 0.1)',
        padding: 16,
        borderRadius: 12
      };
    }
    if (!zone.content.historyTicketStyle) {
      zone.content.historyTicketStyle = {
        fontFamily: zone.textStyle?.fontFamily || 'Plus Jakarta Sans',
        fontSize: 20,
        color: zone.textStyle?.color || '#64748B',
        backgroundColor: 'rgba(200, 200, 200, 0.2)',
        padding: 12,
        borderRadius: 10,
        opacity: 0.7
      };
    }
    if (zone.content.gridColumns === undefined) {
      zone.content.gridColumns = 2;
    }
    if (zone.content.maxTickets === undefined) {
      zone.content.maxTickets = 10;
    }
  }

  return zone;
};

// Liste des 20 polices disponibles
const availableFonts = [
  'Roboto',
  'Open Sans',
  'Montserrat',
  'Lato',
  'Poppins',
  'Raleway',
  'Ubuntu',
  'Playfair Display',
  'Merriweather',
  'Oswald',
  'Source Sans Pro',
  'Nunito',
  'PT Sans',
  'Quicksand',
  'Bebas Neue',
  'Inter',
  'Plus Jakarta Sans',
  'Space Grotesk',
  'DM Sans',
  'Work Sans'
];

const getContentTypeLabel = (type) => {
  const labels = {
    'queue_display': 'Files d\'attente',
    'video': 'Vidéo',
    'image': 'Image',
    'text': 'Texte',
    'clock': 'Horloge',
    'weather': 'Météo',
    'custom_html': 'HTML personnalisé'
  };
  return labels[type] || type;
};

const getLayoutClass = () => {
  const layouts = {
    'single': 'flex-col',
    'split_horizontal': 'flex-row',
    'split_vertical': 'flex-col',
    'grid_2x2': 'grid grid-cols-2 grid-rows-2'
  };
  return layouts[config.value.layout.type] || 'flex-col';
};

const getLogoPositionClass = () => {
  const positions = {
    'top-left': 'top-0 left-0',
    'top-center': 'top-0 left-1/2 -translate-x-1/2',
    'top-right': 'top-0 right-0',
    'bottom-left': 'bottom-0 left-0',
    'bottom-center': 'bottom-0 left-1/2 -translate-x-1/2',
    'bottom-right': 'bottom-0 right-0'
  };
  return positions[config.value.branding.logo.position] || 'top-0 left-0';
};

const getBackgroundStyle = () => {
  if (config.value.branding.backgroundType === 'gradient' && config.value.branding.gradient) {
    return {
      background: `linear-gradient(${config.value.branding.gradient.direction}, ${config.value.branding.gradient.color1}, ${config.value.branding.gradient.color2})`
    };
  } else if (config.value.branding.backgroundType === 'image' && config.value.branding.backgroundImage) {
    return {
      backgroundImage: `url(${config.value.branding.backgroundImage})`,
      backgroundSize: 'cover',
      backgroundPosition: 'center'
    };
  }
  return {
    backgroundColor: config.value.branding.backgroundColor
  };
};

const getTextShadowValue = (textShadow) => {
  if (!textShadow || !textShadow.enabled) return 'none';
  const color = textShadow.color || '#000000';
  const alpha = textShadow.opacity ?? 0.5;
  const r = parseInt(color.slice(1,3), 16);
  const g = parseInt(color.slice(3,5), 16);
  const b = parseInt(color.slice(5,7), 16);
  return `${textShadow.offsetX}px ${textShadow.offsetY}px ${textShadow.blur}px rgba(${r},${g},${b},${alpha})`;
};

const getZoneStyle = (zone) => {
  const style = {};

  // === BACKGROUND ===
  if (zone.style.backgroundType === 'color') {
    style.backgroundColor = zone.style.backgroundColor;
  } else if (zone.style.backgroundType === 'gradient' && zone.style.gradient) {
    style.background = `linear-gradient(${zone.style.gradient.direction || 'to right'}, ${zone.style.gradient.color1 || '#3B82F6'}, ${zone.style.gradient.color2 || '#10B981'})`;
  } else if (zone.style.backgroundType === 'image' && zone.style.backgroundImage) {
    style.backgroundImage = `url(${zone.style.backgroundImage})`;
    style.backgroundSize = 'cover';
    style.backgroundPosition = 'center';
  }

  // === PADDING (avec fallback) ===
  const pt = zone.style.paddingTop ?? zone.style.padding ?? 20;
  const pr = zone.style.paddingRight ?? zone.style.padding ?? 20;
  const pb = zone.style.paddingBottom ?? zone.style.padding ?? 20;
  const pl = zone.style.paddingLeft ?? zone.style.padding ?? 20;
  style.padding = `${pt}px ${pr}px ${pb}px ${pl}px`;

  // === MARGIN ===
  const mt = zone.style.marginTop ?? zone.style.margin ?? 0;
  const mr = zone.style.marginRight ?? zone.style.margin ?? 0;
  const mb = zone.style.marginBottom ?? zone.style.margin ?? 0;
  const ml = zone.style.marginLeft ?? zone.style.margin ?? 0;
  style.margin = `${mt}px ${mr}px ${mb}px ${ml}px`;

  // === BORDER RADIUS (avec fallback) ===
  const rtl = zone.style.borderRadiusTopLeft ?? zone.style.borderRadius ?? 0;
  const rtr = zone.style.borderRadiusTopRight ?? zone.style.borderRadius ?? 0;
  const rbr = zone.style.borderRadiusBottomRight ?? zone.style.borderRadius ?? 0;
  const rbl = zone.style.borderRadiusBottomLeft ?? zone.style.borderRadius ?? 0;
  style.borderRadius = `${rtl}px ${rtr}px ${rbr}px ${rbl}px`;

  // === BORDER ===
  if (zone.style.borderWidth && zone.style.borderWidth > 0) {
    style.border = `${zone.style.borderWidth}px ${zone.style.borderStyle || 'solid'} ${zone.style.borderColor || '#000'}`;
  }

  // === BOX SHADOW ===
  if (zone.style.boxShadow?.enabled) {
    const bs = zone.style.boxShadow;
    const color = bs.color || '#000000';
    const alpha = bs.opacity ?? 0.1;
    const r = parseInt(color.slice(1,3), 16);
    const g = parseInt(color.slice(3,5), 16);
    const b = parseInt(color.slice(5,7), 16);
    style.boxShadow = `${bs.offsetX}px ${bs.offsetY}px ${bs.blur}px ${bs.spread}px rgba(${r},${g},${b},${alpha})`;
  }

  // === OPACITY ===
  if (zone.style.opacity !== undefined && zone.style.opacity !== 1) {
    style.opacity = zone.style.opacity;
  }

  // === FILTERS ===
  const filters = zone.style.filters;
  if (filters) {
    const filterArr = [];
    if (filters.blur > 0) filterArr.push(`blur(${filters.blur}px)`);
    if (filters.brightness !== 100) filterArr.push(`brightness(${filters.brightness}%)`);
    if (filters.contrast !== 100) filterArr.push(`contrast(${filters.contrast}%)`);
    if (filters.saturate !== 100) filterArr.push(`saturate(${filters.saturate}%)`);
    if (filters.hueRotate !== 0) filterArr.push(`hue-rotate(${filters.hueRotate}deg)`);
    if (filterArr.length > 0) style.filter = filterArr.join(' ');
  }

  // === TRANSFORM ===
  const transform = zone.style.transform;
  if (transform) {
    const transformArr = [];
    if (transform.rotate !== 0) transformArr.push(`rotate(${transform.rotate}deg)`);
    if (transform.scale !== 1) transformArr.push(`scale(${transform.scale})`);
    if (transformArr.length > 0) style.transform = transformArr.join(' ');
  }

  // === LAYOUT (position selon le type) ===
  if (config.value.layout.type === 'custom') {
    // Mode libre : position absolue
    style.position = 'absolute';

    // S'assurer que les valeurs sont dans les limites (0-100%)
    const x = Math.max(0, Math.min(100, zone.position.x));
    const y = Math.max(0, Math.min(100, zone.position.y));
    const width = Math.max(5, Math.min(100 - x, zone.position.width));
    const height = Math.max(5, Math.min(100 - y, zone.position.height));

    style.left = x + '%';
    style.top = y + '%';
    style.width = width + '%';
    style.height = height + '%';
    style.boxSizing = 'border-box'; // Important pour que padding/border n'agrandissent pas la zone
  } else if (config.value.layout.type === 'single') {
    style.flex = '1';
    style.boxSizing = 'border-box';
  } else if (config.value.layout.type === 'split_horizontal' || config.value.layout.type === 'split_vertical') {
    style.flex = '1';
    style.boxSizing = 'border-box';
  }

  return style;
};

const addZone = () => {
  saveToHistory();

  // Calculer une position décalée pour éviter les chevauchements
  const existingZones = config.value.zones.length;
  const offset = (existingZones * 5) % 40; // Décalage de 5% à chaque nouvelle zone (max 40%)

  const newZone = {
    id: 'zone-' + Date.now(),
    name: 'Nouvelle Zone',
    position: {
      x: Math.min(offset, 50),
      y: Math.min(offset, 50),
      width: 45,
      height: 45
    },
    content: {
      type: 'text',
      text: 'Nouvelle zone',
      queueIds: [],
      displayMode: 'list',
      ticketFilter: 'BOTH',
      maxTickets: 10,
      gridColumns: 2,
      calledTicketDisplayFormat: 'block',
      ticketDisplayInfo: {
        showNumber: true,
        showQueueName: true,
        showCounterName: true,
        showWaitTime: false
      },
      counterStyle: {
        fontFamily: 'Plus Jakarta Sans',
        fontSize: 38,
        fontWeight: '700',
        color: '#FFFFFF',
        backgroundColor: 'transparent',
        padding: 12,
        borderRadius: 12,
        borderWidth: 2,
        borderColor: '#1e3a8a'
      },
      calledTicketStyle: {
        fontFamily: 'Plus Jakarta Sans',
        fontSize: 48,
        color: '#1E293B'
      },
      waitingTicketStyle: {
        fontFamily: 'Plus Jakarta Sans',
        fontSize: 24,
        color: '#1E293B',
        backgroundColor: 'rgba(255, 255, 255, 0.1)',
        padding: 16,
        borderRadius: 12
      },
      historyTicketStyle: {
        fontFamily: 'Plus Jakarta Sans',
        fontSize: 20,
        color: '#64748B',
        backgroundColor: 'rgba(200, 200, 200, 0.2)',
        padding: 12,
        borderRadius: 10,
        opacity: 0.7
      }
    },
    style: {
      backgroundColor: '#FFFFFF',
      backgroundType: 'color',
      gradient: {
        color1: '#3B82F6',
        color2: '#10B981',
        direction: 'to right'
      },
      backgroundImage: '',
      padding: 20,
      paddingTop: null,
      paddingRight: null,
      paddingBottom: null,
      paddingLeft: null,
      margin: 0,
      marginTop: null,
      marginRight: null,
      marginBottom: null,
      marginLeft: null,
      borderRadius: 12,
      borderRadiusTopLeft: null,
      borderRadiusTopRight: null,
      borderRadiusBottomRight: null,
      borderRadiusBottomLeft: null,
      borderWidth: 0,
      borderColor: '#000000',
      borderStyle: 'solid',
      boxShadow: {
        enabled: false,
        offsetX: 0, offsetY: 4, blur: 6, spread: 0,
        color: '#000000', opacity: 0.1
      },
      textShadow: {
        enabled: false,
        offsetX: 0, offsetY: 2, blur: 4,
        color: '#000000', opacity: 0.5
      },
      opacity: 1,
      filters: {
        blur: 0, brightness: 100, contrast: 100,
        saturate: 100, hueRotate: 0
      },
      transform: { rotate: 0, scale: 1 }
    },
    textStyle: {
      fontFamily: 'Plus Jakarta Sans',
      fontSize: 16,
      color: '#1E293B',
      textAlign: 'left'
    }
  };
  config.value.zones.push(newZone);
  selectedZone.value = newZone;
};

const deleteZone = (zoneId) => {
  if (config.value.zones.length === 1) {
    toast.error("Au moins une zone doit exister");
    return;
  }
  saveToHistory();
  config.value.zones = config.value.zones.filter(z => z.id !== zoneId);
  selectedZone.value = null;
};

// === TEMPLATES ===
const applyTemplate = (template) => {
  saveToHistory();
  const newConfig = JSON.parse(JSON.stringify(template.config));

  // Conserver les propriétés existantes
  newConfig.agencyId = config.value.agencyId;
  newConfig.enabled = config.value.enabled;
  newConfig.refreshInterval = config.value.refreshInterval || 5000;
  newConfig.targetResolution = config.value.targetResolution || { width: 1920, height: 1080 };

  // Initialiser display si manquant (FIX pour le crash "orientation")
  if (!newConfig.display) {
    newConfig.display = config.value.display || { orientation: 'horizontal' };
  }

  // Compléter layout
  if (!newConfig.layout.gridColumns) newConfig.layout.gridColumns = 1;
  if (!newConfig.layout.gridRows) newConfig.layout.gridRows = 1;
  if (!newConfig.layout.padding) newConfig.layout.padding = 24;
  if (!newConfig.layout.gap) newConfig.layout.gap = 20;

  // Remplir le branding avec les champs manquants
  if (!newConfig.branding.logo) {
    newConfig.branding.logo = { show: false, url: '', position: 'top-left', size: 80 };
  }
  if (!newConfig.branding.fonts) {
    newConfig.branding.fonts = { primary: 'Plus Jakarta Sans' };
  }
  if (!newConfig.branding.backgroundImage) {
    newConfig.branding.backgroundImage = '';
  }
  if (!newConfig.branding.gradient) {
    newConfig.branding.gradient = { color1: '#3B82F6', color2: '#10B981', direction: 'to right' };
  }
  // Initialiser colors si manquant (FIX pour le bug d'écran bleu)
  if (!newConfig.branding.colors) {
    newConfig.branding.colors = {
      primary: '#3B82F6',
      text: '#1E293B',
      textSecondary: '#64748B'
    };
  } else {
    if (!newConfig.branding.colors.text) {
      newConfig.branding.colors.text = '#1E293B';
    }
    if (!newConfig.branding.colors.textSecondary) {
      newConfig.branding.colors.textSecondary = '#64748B';
    }
  }

  // Remplir les zones avec tous les champs manquants
  newConfig.zones = newConfig.zones.map(zone => {
    // Compléter content
    if (!zone.content.queueIds) zone.content.queueIds = [];
    if (!zone.content.displayMode) zone.content.displayMode = 'list';

    // Compléter style avec gradient et backgroundImage si manquants
    if (!zone.style.gradient) {
      zone.style.gradient = { color1: '#3B82F6', color2: '#10B981', direction: 'to right' };
    }
    if (!zone.style.backgroundImage) {
      zone.style.backgroundImage = '';
    }

    // Compléter textStyle
    if (!zone.textStyle.textAlign) {
      zone.textStyle.textAlign = 'left';
    }

    // Appliquer la migration complète
    return migrateZoneStyle(zone);
  });

  // Ajouter audio et animations
  if (!newConfig.audio) {
    newConfig.audio = {
      enabled: true,
      volume: 0.8,
      callSound: 'https://assets.mixkit.co/active_storage/sfx/2869/2869-preview.mp3',
      recallSound: 'https://assets.mixkit.co/active_storage/sfx/2869/2869-preview.mp3',
      useVoice: true,
      voiceLang: 'fr-FR'
    };
  }

  if (!newConfig.animations) {
    newConfig.animations = {
      enabled: true,
      ticketTransition: 'fade',
      transitionDuration: 500
    };
  }

  config.value = newConfig;
  selectedZone.value = null;
  toast.success(`Template "${template.name}" appliqué!`);
};

// === HISTORIQUE UNDO/REDO ===
const saveToHistory = () => {
  const snapshot = JSON.parse(JSON.stringify(config.value));
  historyStack.value = historyStack.value.slice(0, historyIndex.value + 1);
  historyStack.value.push(snapshot);
  historyIndex.value++;

  // Limiter l'historique à 50 entrées
  if (historyStack.value.length > 50) {
    historyStack.value.shift();
    historyIndex.value--;
  }
};

const undo = () => {
  if (historyIndex.value > 0) {
    historyIndex.value--;
    config.value = JSON.parse(JSON.stringify(historyStack.value[historyIndex.value]));
    toast.info("Annulé");
  }
};

const redo = () => {
  if (historyIndex.value < historyStack.value.length - 1) {
    historyIndex.value++;
    config.value = JSON.parse(JSON.stringify(historyStack.value[historyIndex.value]));
    toast.info("Rétabli");
  }
};

// === DUPLICATION & COPIE ===
const duplicateZone = (zone) => {
  saveToHistory();
  const newZone = JSON.parse(JSON.stringify(zone));
  newZone.id = 'zone-' + Date.now();
  newZone.name = zone.name + ' (copie)';

  // Décaler légèrement la position en mode custom
  if (config.value.layout.type === 'custom') {
    newZone.position.x = Math.min(newZone.position.x + 5, 95);
    newZone.position.y = Math.min(newZone.position.y + 5, 95);
  }

  config.value.zones.push(newZone);
  selectedZone.value = newZone;
  toast.success("Zone dupliquée!");
};

const centerZone = (zone) => {
  if (!zone || !zone.position) {
    toast.error("Aucune zone sélectionnée");
    return;
  }

  saveToHistory();

  // Calculer la position pour que le centre horizontal de la zone soit au centre de l'écran (50%)
  const centerX = 50 - (zone.position.width / 2);

  // S'assurer que la zone ne dépasse pas les limites horizontales
  zone.position.x = Math.max(0, Math.min(centerX, 100 - zone.position.width));
  // Ne pas toucher à Y

  toast.success(`Zone "${zone.name}" centrée horizontalement!`);
};

const copyStyle = (zone) => {
  copiedStyle.value = JSON.parse(JSON.stringify(zone.style));
  toast.success("Style copié!");
};

const pasteStyle = (zone) => {
  if (!copiedStyle.value) {
    toast.error("Aucun style copié");
    return;
  }
  saveToHistory();
  zone.style = JSON.parse(JSON.stringify(copiedStyle.value));
  toast.success("Style collé!");
};

// === RESET COUNTER STYLE ===
const resetCounterStyle = () => {
  if (!selectedZone.value?.content?.counterStyle) return;
  saveToHistory();
  selectedZone.value.content.counterStyle = {
    fontFamily: selectedZone.value.textStyle?.fontFamily || 'Plus Jakarta Sans',
    fontSize: Math.round((selectedZone.value.textStyle?.fontSize || 32) * 1.2),
    fontWeight: '700',
    color: selectedZone.value.textStyle?.color || '#FFFFFF',
    backgroundColor: 'transparent',
    padding: 12,
    borderRadius: 12,
    borderWidth: 2,
    borderColor: '#1e3a8a'
  };
  toast.success("Style du guichet réinitialisé!");
};

// === CALLED TICKET STYLE CHANGE ===
const onCalledStyleChange = () => {
  if (!selectedZone.value?.content?.calledTicketStyle) return;
  console.log('📝 onCalledStyleChange appelé, nouvelle valeur:', JSON.parse(JSON.stringify(selectedZone.value.content.calledTicketStyle)));

  // Forcer la réactivité en réassignant l'objet complet
  const currentStyle = selectedZone.value.content.calledTicketStyle;
  selectedZone.value.content.calledTicketStyle = {
    ...currentStyle
  };

  // Sauvegarder dans l'historique
  saveToHistory();
};

// === ZOOM ===
const setZoom = (level) => {
  zoomLevel.value = Math.max(50, Math.min(200, level));
};

const getPreviewContainerStyle = () => {
  // Récupérer la résolution cible
  const targetWidth = config.value.targetResolution.width;
  const targetHeight = config.value.targetResolution.height;

  // Calculer un facteur de mise à l'échelle pour que le preview rentre dans l'écran
  // On vise environ 800px pour la plus grande dimension à 100% de zoom
  const maxPreviewDimension = 800;
  const scaleFactor = Math.min(maxPreviewDimension / targetWidth, maxPreviewDimension / targetHeight);

  // Dimensions du preview à 100% de zoom
  const baseWidth = targetWidth * scaleFactor;
  const baseHeight = targetHeight * scaleFactor;

  // Appliquer le zoom
  const finalWidth = baseWidth * (zoomLevel.value / 100);
  const finalHeight = baseHeight * (zoomLevel.value / 100);

  return {
    width: `${finalWidth}px`,
    height: `${finalHeight}px`,
    transform: 'scale(1)'
  };
};

const getAspectRatio = () => {
  const width = config.value.targetResolution.width;
  const height = config.value.targetResolution.height;

  // Calculer le GCD (Plus Grand Commun Diviseur)
  const gcd = (a, b) => b === 0 ? a : gcd(b, a % b);
  const divisor = gcd(width, height);

  const ratioW = width / divisor;
  const ratioH = height / divisor;

  // Vérifier les ratios communs
  if (ratioW === 16 && ratioH === 9) return '16:9';
  if (ratioW === 9 && ratioH === 16) return '9:16';
  if (ratioW === 4 && ratioH === 3) return '4:3';
  if (ratioW === 3 && ratioH === 4) return '3:4';
  if (ratioW === 21 && ratioH === 9) return '21:9';
  if (ratioW === 1 && ratioH === 1) return '1:1';

  // Sinon afficher le ratio simplifié
  return `${ratioW}:${ratioH}`;
};

const getZonesContainerStyle = () => {
  if (config.value.layout.type === 'custom') {
    // En mode custom, pas de padding pour que les positions % soient correctes
    return {};
  }
  // En mode flex, ajouter le gap et le padding
  return {
    gap: config.value.layout.gap + 'px',
    padding: (config.value.layout.padding || 24) + 'px'
  };
};

const getZoneContentPadding = (zone) => {
  const pt = zone.style.paddingTop ?? zone.style.padding ?? 20;
  return { padding: `${pt}px` };
};

const setResolution = (width, height) => {
  saveToHistory();
  config.value.targetResolution.width = width;
  config.value.targetResolution.height = height;

  // Ajuster automatiquement l'orientation selon la résolution
  if (width > height) {
    config.value.display.orientation = 'horizontal';
  } else {
    config.value.display.orientation = 'vertical';
  }

  toast.success(`Résolution changée à ${width}×${height}`);
};



// === EXPORT/IMPORT ===
const exportConfig = () => {
  const dataStr = JSON.stringify(config.value, null, 2);
  const dataBlob = new Blob([dataStr], { type: 'application/json' });
  const url = URL.createObjectURL(dataBlob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `screen-config-${Date.now()}.json`;
  link.click();
  URL.revokeObjectURL(url);
  toast.success("Configuration exportée!");
};

const importConfig = (event) => {
  const file = event.target.files[0];
  if (!file) return;

  const reader = new FileReader();
  reader.onload = (e) => {
    try {
      const imported = JSON.parse(e.target.result);
      saveToHistory();

      // Nettoyer les IDs MongoDB pour éviter les conflits
      delete imported._id; // Supprimer l'ID de la config
      delete imported.__v; // Supprimer la version MongoDB
      delete imported.deviceId; // Supprimer le deviceId (sera assigné automatiquement si nécessaire)
      delete imported.createdAt; // Supprimer les timestamps
      delete imported.updatedAt;

      // Remplacer par l'agencyId actuelle
      imported.agencyId = config.value.agencyId;

      // Nettoyer les zones : supprimer les _id, régénérer les IDs, vider les queueIds
      imported.zones = imported.zones.map((zone) => {
        // Supprimer les IDs MongoDB
        delete zone._id;

        // Générer un nouvel ID unique pour la zone
        zone.id = 'zone-' + Date.now() + '-' + Math.random().toString(36).substr(2, 9);

        // Vider les queueIds car ils sont spécifiques à chaque agence
        if (zone.content && zone.content.queueIds) {
          zone.content.queueIds = [];
        }

        // Appliquer la migration de style
        return migrateZoneStyle(zone);
      });

      // Détecter le preset de banner
      if (imported.banner) {
        const bgColor = imported.banner.normal?.backgroundColor;
        if (bgColor === 'rgba(255, 255, 255, 0.95)' || bgColor === 'rgb(255, 255, 255)') {
          bannerStylePreset.value = 'default';
        } else if (bgColor === 'rgba(240, 253, 244, 0.95)' || bgColor?.includes('240, 253, 244')) {
          bannerStylePreset.value = 'green';
        } else if (bgColor === 'rgba(30, 41, 59, 0.95)' || bgColor?.includes('30, 41, 59')) {
          bannerStylePreset.value = 'dark';
        } else if (bgColor === 'rgba(255, 247, 237, 0.95)' || bgColor?.includes('255, 247, 237')) {
          bannerStylePreset.value = 'orange';
        } else if (bgColor === 'rgba(250, 245, 255, 0.95)' || bgColor?.includes('250, 245, 255')) {
          bannerStylePreset.value = 'purple';
        } else {
          bannerStylePreset.value = 'custom';
        }
      }

      config.value = imported;
      toast.success("Configuration importée!");
    } catch (err) {
      toast.error("Fichier invalide");
    }
  };
  reader.readAsText(file);
};

// === PRESETS DE BANNER ===
const applyBannerPreset = () => {
  saveToHistory();

  const presets = {
    default: {
      normal: {
        backgroundColor: 'rgba(255, 255, 255, 0.95)',
        borderColor: 'rgba(255, 255, 255, 0.2)',
        textColor: 'rgb(15, 23, 42)',
        iconColor: '#1e3a8a',
        iconBackgroundColor: 'rgb(239, 246, 255)',
        badgeColor: 'rgb(29, 78, 216)',
        badgeBorderColor: 'rgb(29, 78, 216)'
      },
      recall: {
        backgroundColor: 'rgb(15, 23, 42)',
        borderColor: 'rgba(245, 158, 11, 0.4)',
        textColor: 'rgb(251, 191, 36)',
        iconColor: '#f59e0b',
        iconBackgroundColor: 'rgba(245, 158, 11, 0.1)',
        badgeColor: 'rgb(245, 158, 11)',
        badgeBorderColor: 'rgb(245, 158, 11)',
        recallBadgeColor: 'rgb(245, 158, 11)'
      },
      borderRadius: 32,
      borderWidth: 1,
      shadowOpacity: 0.3,
      displayDuration: { normal: 5, recall: 7 },
      fontFamily: 'Plus Jakarta Sans',
      ticketNumberSize: 144,
      queueNameSize: 24,
      badgeSize: 56,
      instructionSize: 20,
      height: 180
    },
    green: {
      normal: {
        backgroundColor: 'rgba(240, 253, 244, 0.95)',
        borderColor: 'rgba(34, 197, 94, 0.3)',
        textColor: 'rgb(22, 101, 52)',
        iconColor: '#16a34a',
        iconBackgroundColor: 'rgb(220, 252, 231)',
        badgeColor: 'rgb(22, 163, 74)',
        badgeBorderColor: 'rgb(22, 163, 74)'
      },
      recall: {
        backgroundColor: 'rgb(20, 83, 45)',
        borderColor: 'rgba(74, 222, 128, 0.4)',
        textColor: 'rgb(134, 239, 172)',
        iconColor: '#4ade80',
        iconBackgroundColor: 'rgba(74, 222, 128, 0.1)',
        badgeColor: 'rgb(74, 222, 128)',
        badgeBorderColor: 'rgb(74, 222, 128)',
        recallBadgeColor: 'rgb(74, 222, 128)'
      },
      borderRadius: 32,
      borderWidth: 1,
      shadowOpacity: 0.3,
      displayDuration: { normal: 5, recall: 7 },
      fontFamily: 'Plus Jakarta Sans',
      ticketNumberSize: 144,
      queueNameSize: 24,
      badgeSize: 56,
      instructionSize: 20,
      height: 180
    },
    dark: {
      normal: {
        backgroundColor: 'rgba(30, 41, 59, 0.95)',
        borderColor: 'rgba(148, 163, 184, 0.3)',
        textColor: 'rgb(226, 232, 240)',
        iconColor: '#60a5fa',
        iconBackgroundColor: 'rgba(96, 165, 250, 0.1)',
        badgeColor: 'rgb(96, 165, 250)',
        badgeBorderColor: 'rgb(96, 165, 250)'
      },
      recall: {
        backgroundColor: 'rgb(15, 23, 42)',
        borderColor: 'rgba(251, 146, 60, 0.4)',
        textColor: 'rgb(253, 186, 116)',
        iconColor: '#fb923c',
        iconBackgroundColor: 'rgba(251, 146, 60, 0.1)',
        badgeColor: 'rgb(251, 146, 60)',
        badgeBorderColor: 'rgb(251, 146, 60)',
        recallBadgeColor: 'rgb(251, 146, 60)'
      },
      borderRadius: 32,
      borderWidth: 1,
      shadowOpacity: 0.4,
      displayDuration: { normal: 5, recall: 7 },
      fontFamily: 'Plus Jakarta Sans',
      ticketNumberSize: 144,
      queueNameSize: 24,
      badgeSize: 56,
      instructionSize: 20,
      height: 180
    },
    orange: {
      normal: {
        backgroundColor: 'rgba(255, 247, 237, 0.95)',
        borderColor: 'rgba(249, 115, 22, 0.3)',
        textColor: 'rgb(124, 45, 18)',
        iconColor: '#f97316',
        iconBackgroundColor: 'rgb(254, 215, 170)',
        badgeColor: 'rgb(249, 115, 22)',
        badgeBorderColor: 'rgb(249, 115, 22)'
      },
      recall: {
        backgroundColor: 'rgb(124, 45, 18)',
        borderColor: 'rgba(251, 146, 60, 0.4)',
        textColor: 'rgb(254, 215, 170)',
        iconColor: '#fb923c',
        iconBackgroundColor: 'rgba(251, 146, 60, 0.1)',
        badgeColor: 'rgb(251, 146, 60)',
        badgeBorderColor: 'rgb(251, 146, 60)',
        recallBadgeColor: 'rgb(251, 146, 60)'
      },
      borderRadius: 32,
      borderWidth: 1,
      shadowOpacity: 0.3,
      displayDuration: { normal: 5, recall: 7 },
      fontFamily: 'Plus Jakarta Sans',
      ticketNumberSize: 144,
      queueNameSize: 24,
      badgeSize: 56,
      instructionSize: 20,
      height: 180
    },
    purple: {
      normal: {
        backgroundColor: 'rgba(250, 245, 255, 0.95)',
        borderColor: 'rgba(168, 85, 247, 0.3)',
        textColor: 'rgb(88, 28, 135)',
        iconColor: '#a855f7',
        iconBackgroundColor: 'rgb(243, 232, 255)',
        badgeColor: 'rgb(168, 85, 247)',
        badgeBorderColor: 'rgb(168, 85, 247)'
      },
      recall: {
        backgroundColor: 'rgb(88, 28, 135)',
        borderColor: 'rgba(192, 132, 252, 0.4)',
        textColor: 'rgb(233, 213, 255)',
        iconColor: '#c084fc',
        iconBackgroundColor: 'rgba(192, 132, 252, 0.1)',
        badgeColor: 'rgb(192, 132, 252)',
        badgeBorderColor: 'rgb(192, 132, 252)',
        recallBadgeColor: 'rgb(192, 132, 252)'
      },
      borderRadius: 32,
      borderWidth: 1,
      shadowOpacity: 0.3,
      displayDuration: { normal: 5, recall: 7 },
      fontFamily: 'Plus Jakarta Sans',
      ticketNumberSize: 144,
      queueNameSize: 24,
      badgeSize: 56,
      instructionSize: 20,
      height: 180
    }
  };

  if (bannerStylePreset.value !== 'custom') {
    const preset = presets[bannerStylePreset.value];
    if (preset) {
      config.value.banner = preset;
      config.value.banner.preset = bannerStylePreset.value; // Sauvegarder le preset sélectionné
      toast.success(`Style "${bannerStylePreset.value}" appliqué!`);
    }
  } else {
    // En mode custom, sauvegarder le preset comme "custom"
    config.value.banner.preset = 'custom';
  }
};

// Réinitialiser à une page vierge
const resetToBlank = () => {
  // Confirmation avant de tout supprimer
  const confirmed = confirm(
    "⚠️ Attention!\n\n" +
    "Cette action va supprimer TOUTES les zones et réinitialiser le design à une page vierge.\n\n" +
    "Cette action ne sera envoyée au backend que si vous cliquez sur 'Publier les changements'.\n\n" +
    "Voulez-vous continuer?"
  );

  if (!confirmed) {
    return;
  }

  // Sauvegarder dans l'historique avant de tout supprimer
  saveToHistory();

  // Créer une configuration vierge avec une seule zone par défaut
  const blankConfig = {
    agencyId: config.value.agencyId,
    enabled: config.value.enabled,
    display: config.value.display || { orientation: 'horizontal' },
    targetResolution: config.value.targetResolution || { width: 1920, height: 1080 },
    refreshInterval: config.value.refreshInterval || 5000,
    layout: {
      type: 'single',
      gridColumns: 1,
      gridRows: 1,
      gap: 20,
      padding: 24
    },
    branding: {
      backgroundColor: '#0f172a',
      backgroundType: 'color',
      gradient: { color1: '#3B82F6', color2: '#10B981', direction: 'to right' },
      backgroundImage: '',
      colors: {
        primary: '#3B82F6',
        text: '#FFFFFF',
        textSecondary: '#94A3B8'
      },
      fonts: {
        primary: 'Plus Jakarta Sans'
      },
      logo: {
        show: false,
        url: '',
        position: 'top-left',
        size: 80
      }
    },
    audio: {
      enabled: true,
      volume: 0.8,
      callSound: 'https://assets.mixkit.co/active_storage/sfx/2869/2869-preview.mp3',
      recallSound: 'https://assets.mixkit.co/active_storage/sfx/2869/2869-preview.mp3',
      useVoice: true,
      voiceLang: 'fr-FR'
    },
    animations: {
      enabled: true,
      ticketTransition: 'fade',
      transitionDuration: 500
    },
    banner: {
      normal: {
        backgroundColor: 'rgba(255, 255, 255, 0.95)',
        borderColor: 'rgba(255, 255, 255, 0.2)',
        textColor: 'rgb(15, 23, 42)',
        iconColor: '#1e3a8a',
        iconBackgroundColor: 'rgb(239, 246, 255)',
        badgeColor: 'rgb(29, 78, 216)',
        badgeBorderColor: 'rgb(29, 78, 216)'
      },
      recall: {
        backgroundColor: 'rgb(15, 23, 42)',
        borderColor: 'rgba(245, 158, 11, 0.4)',
        textColor: 'rgb(251, 191, 36)',
        iconColor: '#f59e0b',
        iconBackgroundColor: 'rgba(245, 158, 11, 0.1)',
        badgeColor: 'rgb(245, 158, 11)',
        badgeBorderColor: 'rgb(245, 158, 11)',
        recallBadgeColor: 'rgb(245, 158, 11)'
      },
      borderRadius: 32,
      borderWidth: 1,
      shadowOpacity: 0.3,
      displayDuration: {
        normal: 5,
        recall: 7
      },
      fontFamily: 'Plus Jakarta Sans',
      ticketNumberSize: 144,
      queueNameSize: 24,
      badgeSize: 56,
      instructionSize: 20,
      height: 180
    },
    zones: [
      {
        id: 'zone-' + Date.now(),
        name: 'Zone Principale',
        position: { x: 0, y: 0, width: 100, height: 100, z: 10 },
        content: {
          type: 'queue_display',
          queueIds: [],
          displayMode: 'list',
          ticketFilter: 'BOTH',
          maxTickets: 10,
          gridColumns: 2,
          calledTicketDisplayFormat: 'block',
          ticketDisplayInfo: {
            showNumber: true,
            showQueueName: true,
            showCounterName: true,
            showWaitTime: false
          },
          calledTicketStyle: {
            fontFamily: 'Plus Jakarta Sans',
            fontSize: 48,
            color: '#FFFFFF'
          },
          waitingTicketStyle: {
            fontFamily: 'Plus Jakarta Sans',
            fontSize: 24,
            color: '#FFFFFF',
            backgroundColor: 'rgba(255, 255, 255, 0.1)',
            padding: 16,
            borderRadius: 12
          }
        },
        style: {
          backgroundColor: '#1E293B',
          backgroundType: 'color',
          gradient: {
            color1: '#3B82F6',
            color2: '#10B981',
            direction: 'to right'
          },
          backgroundImage: '',
          padding: 20,
          paddingTop: 20,
          paddingRight: 20,
          paddingBottom: 20,
          paddingLeft: 20,
          borderRadius: 12,
          borderRadiusTopLeft: 12,
          borderRadiusTopRight: 12,
          borderRadiusBottomRight: 12,
          borderRadiusBottomLeft: 12,
          borderWidth: 0,
          borderStyle: 'solid',
          borderColor: '#000000',
          boxShadow: {
            enabled: false,
            offsetX: 0,
            offsetY: 4,
            blur: 6,
            spread: 0,
            color: '#000000',
            opacity: 0.1
          },
          textShadow: {
            enabled: false,
            offsetX: 0,
            offsetY: 2,
            blur: 4,
            color: '#000000',
            opacity: 0.5
          },
          opacity: 1,
          filters: {
            blur: 0,
            brightness: 100,
            contrast: 100,
            saturate: 100,
            hueRotate: 0
          },
          transform: {
            rotate: 0,
            scale: 1
          }
        },
        textStyle: {
          fontFamily: 'Plus Jakarta Sans',
          fontSize: 24,
          color: '#FFFFFF',
          textAlign: 'left'
        }
      }
    ]
  };

  // Appliquer la configuration vierge
  config.value = blankConfig;
  selectedZone.value = null;

  toast.success("✨ Page vierge créée! N'oubliez pas de publier pour sauvegarder.");
};

const onContentTypeChange = () => {
  if (!selectedZone.value) return;

  // Si on passe à queue_display, initialiser les champs nécessaires
  if (selectedZone.value.content.type === 'queue_display') {
    if (!selectedZone.value.content.queueIds) {
      selectedZone.value.content.queueIds = [];
    }
    if (!selectedZone.value.content.ticketFilter) {
      selectedZone.value.content.ticketFilter = 'BOTH';
      console.log('[ScreenEditor] ticketFilter initialisé à BOTH');
    }
    if (!selectedZone.value.content.displayMode) {
      selectedZone.value.content.displayMode = 'list';
    }
    if (!selectedZone.value.content.maxTickets) {
      selectedZone.value.content.maxTickets = 10;
    }
    if (!selectedZone.value.content.gridColumns) {
      selectedZone.value.content.gridColumns = 2;
    }
    if (!selectedZone.value.content.calledTicketStyle) {
      selectedZone.value.content.calledTicketStyle = {
        fontFamily: selectedZone.value.textStyle?.fontFamily || 'Plus Jakarta Sans',
        fontSize: 48,
        color: selectedZone.value.textStyle?.color || '#1E293B'
      };
    }
    if (!selectedZone.value.content.waitingTicketStyle) {
      selectedZone.value.content.waitingTicketStyle = {
        fontFamily: selectedZone.value.textStyle?.fontFamily || 'Plus Jakarta Sans',
        fontSize: 24,
        color: selectedZone.value.textStyle?.color || '#1E293B',
        backgroundColor: 'rgba(255, 255, 255, 0.1)',
        padding: 16,
        borderRadius: 12
      };
    }
    if (!selectedZone.value.content.historyTicketStyle) {
      selectedZone.value.content.historyTicketStyle = {
        fontFamily: selectedZone.value.textStyle?.fontFamily || 'Plus Jakarta Sans',
        fontSize: 20,
        color: selectedZone.value.textStyle?.color || '#64748B',
        backgroundColor: 'rgba(200, 200, 200, 0.2)',
        padding: 12,
        borderRadius: 10,
        opacity: 0.7
      };
    }
  }
};

const toggleQueue = (queueId) => {
  if (!selectedZone.value.content.queueIds) {
    selectedZone.value.content.queueIds = [];
  }
  const idx = selectedZone.value.content.queueIds.indexOf(queueId);
  if (idx > -1) {
    selectedZone.value.content.queueIds.splice(idx, 1);
  } else {
    selectedZone.value.content.queueIds.push(queueId);
  }
};



const fetchQueues = async () => {
  try {
    const token = localStorage.getItem("superAdminToken");
    const res = await fetch(`https://api.datafuse.fr/api/super-admin/queues/${agencyId}`, {
      headers: { 'Authorization': `Bearer ${token}` }
    });
    if (res.ok) {
      queues.value = await res.json();
    }
  } catch (err) {
    console.error("Erreur queues:", err);
  }
};

// ✅ NOUVEAU : Charger la liste des écrans disponibles pour cette agence
const fetchAvailableScreens = async () => {
  try {
    const token = localStorage.getItem("superAdminToken");
    const res = await fetch(`https://api.datafuse.fr/api/super-admin/screens/${agencyId}`, {
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` }
    });

    if (res.ok) {
      availableScreens.value = await res.json();
      console.log(`✅ ${availableScreens.value.length} écran(s) chargé(s)`);
    }
  } catch (err) {
    console.error("Erreur chargement écrans:", err);
  }
};

// ✅ MODIFIÉ : Charger la config d'un écran spécifique ou la config par défaut
const loadScreenConfig = async () => {
  // Attendre que Vue mette à jour selectedDeviceId avant de charger la config
  await nextTick();
  console.log('🔄 Changement d\'écran détecté - selectedDeviceId:', selectedDeviceId.value);
  await fetchConfig();
};

const fetchConfig = async () => {
  try {
    const token = localStorage.getItem("superAdminToken");
    // ✅ Ajouter deviceId dans la query si un écran est sélectionné
    const url = selectedDeviceId.value
      ? `https://api.datafuse.fr/api/super-admin/screen-config/${agencyId}?deviceId=${selectedDeviceId.value}`
      : `https://api.datafuse.fr/api/super-admin/screen-config/${agencyId}`;

    console.log('📡 Chargement config depuis:', url);
    console.log('📱 selectedDeviceId actuel:', selectedDeviceId.value);

    const res = await fetch(url, {
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` }
    });
    if (res.status === 404) {
      return;
    }
    const data = await res.json();

    // --- AJOUTEZ CE BLOC DE SÉCURITÉ ICI ---
    if (!data.branding) {
      data.branding = {};
    }
    if (!data.branding.logo) {
      data.branding.logo = {
        show: false,
        url: '',
        position: 'top-left',
        size: 80
      };
    }

    // Initialiser les valeurs manquantes pour les gradients
    if (!data.branding.gradient) {
      data.branding.gradient = { color1: '#3B82F6', color2: '#10B981', direction: 'to right' };
    }
    if (!data.branding.backgroundImage) {
      data.branding.backgroundImage = '';
    }

    // Initialiser les gradients pour chaque zone
    data.zones.forEach(zone => {
      if (!zone.style.gradient) {
        zone.style.gradient = { color1: '#3B82F6', color2: '#10B981', direction: 'to right' };
      }
      if (!zone.style.backgroundImage) {
        zone.style.backgroundImage = '';
      }
    });

    // Appliquer la migration automatique pour les nouveaux champs
    if (data.zones && Array.isArray(data.zones)) {
      data.zones = data.zones.map(migrateZoneStyle);
    }

    // Migrer les propriétés audio et animations
    if (!data.audio) {
      data.audio = {
        enabled: true,
        volume: 0.8,
        callSound: 'https://assets.mixkit.co/active_storage/sfx/2869/2869-preview.mp3',
        recallSound: 'https://assets.mixkit.co/active_storage/sfx/2869/2869-preview.mp3',
        useVoice: true,
        voiceLang: 'fr-FR'
      };
    }

    if (!data.animations) {
      data.animations = {
        enabled: true,
        ticketTransition: 'fade',
        transitionDuration: 500
      };
    }

    // Migrer banner si manquant
    if (!data.banner) {
      data.banner = {
        normal: {
          backgroundColor: 'rgba(255, 255, 255, 0.95)',
          borderColor: 'rgba(255, 255, 255, 0.2)',
          textColor: 'rgb(15, 23, 42)',
          iconColor: '#1e3a8a',
          iconBackgroundColor: 'rgb(239, 246, 255)',
          badgeColor: 'rgb(29, 78, 216)',
          badgeBorderColor: 'rgb(29, 78, 216)'
        },
        recall: {
          backgroundColor: 'rgb(15, 23, 42)',
          borderColor: 'rgba(245, 158, 11, 0.4)',
          textColor: 'rgb(251, 191, 36)',
          iconColor: '#f59e0b',
          iconBackgroundColor: 'rgba(245, 158, 11, 0.1)',
          badgeColor: 'rgb(245, 158, 11)',
          badgeBorderColor: 'rgb(245, 158, 11)',
          recallBadgeColor: 'rgb(245, 158, 11)'
        },
        borderRadius: 32,
        borderWidth: 1,
        shadowOpacity: 0.3,
        displayDuration: {
          normal: 5,
          recall: 7
        }
      };
    }

    // Migrer display.orientation et targetResolution
    if (!data.display) {
      data.display = {
        orientation: 'horizontal'
      };
    }
    if (!data.targetResolution) {
      data.targetResolution = {
        width: 1920,
        height: 1080
      };
    }

    // Migrer layout.padding si manquant
    if (!data.layout) {
      data.layout = {
        type: 'single',
        gap: 20,
        padding: 24
      };
    } else {
      // Si layout existe mais padding n'existe pas, l'ajouter
      if (data.layout.padding === undefined) {
        data.layout.padding = 24;
      }
      if (data.layout.gap === undefined) {
        data.layout.gap = 20;
      }
    }

    // Restaurer le preset de banner sauvegardé
    if (data.banner) {
      // Si le preset est sauvegardé dans la config, l'utiliser
      if (data.banner.preset) {
        bannerStylePreset.value = data.banner.preset;
      } else {
        // Sinon, détecter le preset à partir de la couleur (legacy)
        const bgColor = data.banner.normal?.backgroundColor;
        if (bgColor === 'rgba(255, 255, 255, 0.95)' || bgColor === 'rgb(255, 255, 255)') {
          bannerStylePreset.value = 'default';
        } else if (bgColor === 'rgba(240, 253, 244, 0.95)' || bgColor?.includes('240, 253, 244')) {
          bannerStylePreset.value = 'green';
        } else if (bgColor === 'rgba(30, 41, 59, 0.95)' || bgColor?.includes('30, 41, 59')) {
          bannerStylePreset.value = 'dark';
        } else if (bgColor === 'rgba(255, 247, 237, 0.95)' || bgColor?.includes('255, 247, 237')) {
          bannerStylePreset.value = 'orange';
        } else if (bgColor === 'rgba(250, 245, 255, 0.95)' || bgColor?.includes('250, 245, 255')) {
          bannerStylePreset.value = 'purple';
        } else {
          bannerStylePreset.value = 'custom';
        }
        // Sauvegarder le preset détecté dans la config
        data.banner.preset = bannerStylePreset.value;
      }
    }

    // DEBUG: Afficher ce qui est reçu du backend
    console.log('📥 Chargement depuis backend - zones:', data.zones?.map(z => ({
      name: z.name,
      ticketFilter: z.content?.ticketFilter,
      calledTicketStyle: z.content?.calledTicketStyle
    })));

    // Initialiser calledTicketStyle pour les zones qui n'en ont pas
    if (data.zones) {
      data.zones = data.zones.map(zone => {
        if (zone.content?.type === 'queue_display' &&
            (zone.content.ticketFilter === 'CALLED' || zone.content.ticketFilter === 'BOTH')) {
          if (!zone.content.calledTicketStyle) {
            console.log(`⚠️ Zone "${zone.name}" - calledTicketStyle manquant, initialisation par défaut`);
            // Créer une nouvelle zone avec calledTicketStyle pour forcer la réactivité
            return {
              ...zone,
              content: {
                ...zone.content,
                calledTicketStyle: {
                  fontFamily: zone.textStyle?.fontFamily || 'Poppins',
                  fontSize: zone.textStyle?.fontSize || 48,
                  color: zone.textStyle?.color || '#FFFFFF'
                }
              }
            };
          } else {
            console.log(`✅ Zone "${zone.name}" - calledTicketStyle existant:`, zone.content.calledTicketStyle);
          }
        }
        return zone;
      });
    }

    config.value = data;
  } catch (err) {
    console.error("Erreur config:", err);
  }
};

const logoEnabled = computed({
  get: () => config.value?.branding?.logo?.show || false,
  set: (val) => {
    // On s'assure que la structure existe avant d'assigner
    if (!config.value.branding) config.value.branding = {};
    if (!config.value.branding.logo) {
      config.value.branding.logo = { url: '', position: 'top-left', size: 80 };
    }
    config.value.branding.logo.show = val;
  }
});

const saveConfig = async () => {
  const idToast = toast.loading("Enregistrement...");
  try {
    const token = localStorage.getItem("superAdminToken");

    // DEBUG: Afficher les calledTicketStyle avant sauvegarde
    console.log('💾 Sauvegarde - config.value.zones:', config.value.zones.map(z => ({
      name: z.name,
      ticketFilter: z.content?.ticketFilter,
      calledTicketStyle: z.content?.calledTicketStyle
    })));

    // ✅ Ajouter deviceId dans la query si un écran est sélectionné
    const url = selectedDeviceId.value
      ? `https://api.datafuse.fr/api/super-admin/screen-config/${agencyId}?deviceId=${selectedDeviceId.value}`
      : `https://api.datafuse.fr/api/super-admin/screen-config/${agencyId}`;

    const res = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
      body: JSON.stringify(config.value)
    });
    if (!res.ok) throw new Error("Erreur sauvegarde");
    toast.update(idToast, { render: "🚀 Configuration publiée !", type: "success", isLoading: false, autoClose: 3000 });
  } catch (err) {
    toast.update(idToast, { render: err.message, type: "error", isLoading: false, autoClose: 3000 });
  }
};

// Watcher pour initialiser calledTicketStyle quand on sélectionne une zone
watch(selectedZone, (zone) => {
  if (zone?.content?.type === 'queue_display' &&
      (zone.content.ticketFilter === 'CALLED' || zone.content.ticketFilter === 'BOTH')) {
    if (!zone.content.calledTicketStyle) {
      console.log(`🔧 Watcher - Initialisation calledTicketStyle pour zone "${zone.name}"`);
      // Initialiser avec textStyle comme base de manière réactive
      const newStyle = {
        fontFamily: zone.textStyle?.fontFamily || 'Poppins',
        fontSize: zone.textStyle?.fontSize || 48,
        color: zone.textStyle?.color || '#FFFFFF'
      };
      // Utiliser Object.assign ou réaffectation pour forcer la réactivité
      zone.content.calledTicketStyle = newStyle;

      // Déclencher une mise à jour de l'historique
      saveToHistory();
    } else {
      console.log(`✅ Watcher - Zone "${zone.name}" a déjà calledTicketStyle:`, zone.content.calledTicketStyle);
    }
  }
}, { immediate: true });

onMounted(async () => {
  // ✅ Charger la liste des écrans d'abord
  await fetchAvailableScreens();

  // ✅ Si deviceId est dans l'URL, pré-sélectionner cet écran
  if (route.query.deviceId) {
    selectedDeviceId.value = route.query.deviceId;
    console.log('📍 Écran pré-sélectionné depuis URL:', selectedDeviceId.value);
  }

  fetchConfig();
  fetchQueues();

  // Initialiser l'historique
  saveToHistory();

  // Raccourcis clavier
  const handleKeyboard = (e) => {
    // Ctrl+Z / Cmd+Z pour Undo
    if ((e.ctrlKey || e.metaKey) && e.key === 'z' && !e.shiftKey) {
      e.preventDefault();
      undo();
    }
    // Ctrl+Y / Ctrl+Shift+Z / Cmd+Shift+Z pour Redo
    if (((e.ctrlKey || e.metaKey) && e.key === 'y') || ((e.ctrlKey || e.metaKey) && e.shiftKey && e.key === 'z')) {
      e.preventDefault();
      redo();
    }
    // Ctrl+D / Cmd+D pour dupliquer la zone sélectionnée
    if ((e.ctrlKey || e.metaKey) && e.key === 'd' && selectedZone.value) {
      e.preventDefault();
      duplicateZone(selectedZone.value);
    }
    // Ctrl+C / Cmd+C pour copier le style
    if ((e.ctrlKey || e.metaKey) && e.key === 'c' && selectedZone.value) {
      e.preventDefault();
      copyStyle(selectedZone.value);
    }
    // Ctrl+V / Cmd+V pour coller le style
    if ((e.ctrlKey || e.metaKey) && e.key === 'v' && selectedZone.value && copiedStyle.value) {
      e.preventDefault();
      pasteStyle(selectedZone.value);
    }
    // Supprimer avec touche Delete
    if (e.key === 'Delete' && selectedZone.value && config.value.zones.length > 1) {
      e.preventDefault();
      deleteZone(selectedZone.value.id);
    }
  };

  window.addEventListener('keydown', handleKeyboard);

  // Nettoyer au démontage
  return () => {
    window.removeEventListener('keydown', handleKeyboard);
  };
});
</script>

<style scoped>
.font-sans { font-family: 'Plus Jakarta Sans', sans-serif; }

/* Scrollbar personnalisée */
.custom-scrollbar::-webkit-scrollbar {
  width: 6px;
}

.custom-scrollbar::-webkit-scrollbar-track {
  background: transparent;
}

.custom-scrollbar::-webkit-scrollbar-thumb {
  background: #cbd5e1;
  border-radius: 10px;
}

.custom-scrollbar::-webkit-scrollbar-thumb:hover {
  background: #94a3b8;
}

/* Animations */
@keyframes slide-in-from-top-2 {
  from {
    opacity: 0;
    transform: translateY(-8px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes fade-in {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

.animate-in {
  animation-duration: 0.3s;
  animation-fill-mode: both;
}

.slide-in-from-top-2 {
  animation-name: slide-in-from-top-2;
}

.fade-in {
  animation-name: fade-in;
}
</style>
